import type { Point, Wall } from '../types'; // Adjust path as necessary
import { WALL_THICKNESS, GRID_SIZE } from '../constants/floorPlanConstants';
import { checkConnectorExists, createSafeConnector } from './connectorDuplicationPrevention';
import { snapPointToGrid } from './floorPlanUtils';

// If there are any other broadly useful geometric constants or small helper functions 
// from FloorPlanCreator.tsx that calculateResizedWalls depends on 
// (and are not already in a more general utility file), they could also be moved here.
// For now, we'll assume calculateResizedWalls is mostly self-contained or uses helpers defined within it.

// Helper function to create extrusion walls when moving split walls
// Returns null for inward resize to indicate standard resize logic should be used
const calculateSplitWallExtrusion = (
  roomWallsInput: Wall[],
  draggedWallSegmentProp: Wall,
  resizeHandleDirection: string,
  targetPoint: Point,
  allAvailableWalls?: Wall[] // ⚡ NEW: Additional walls for connector detection (tempWalls + currentWalls)
): Wall[] | null => {
  
  // 🛡️ METADATA PRESERVATION: Validate and preserve wall metadata during extrusion
  const originalWallMetadata = roomWallsInput.map(w => ({
    tempId: (w as any).tempId,
    originatingRoomId: (w as any).originatingRoomId,
    assignedRoomIds: (w as any).assignedRoomIds,
    parentRoomIds: (w as any).parentRoomIds,
    parentWallId: (w as any).parentWallId,
    isSplitOrigin: (w as any).isSplitOrigin,
    isMainExtrusionSegment: (w as any).isMainExtrusionSegment,
    isExtrusionConnector: (w as any).isExtrusionConnector
  }));
  
  // Debug logging disabled during dragging preview for performance
  // console.log('🔧 [calculateSplitWallExtrusion] ENTRY', {
  //   wallBeingDragged: `(${draggedWallSegmentProp.start.x.toFixed(1)},${draggedWallSegmentProp.start.y.toFixed(1)})->(${draggedWallSegmentProp.end.x.toFixed(1)},${draggedWallSegmentProp.end.y.toFixed(1)})`,
  //   isSplitOrigin: (draggedWallSegmentProp as any).isSplitOrigin,
  //   parentWallId: (draggedWallSegmentProp as any).parentWallId,
  //   roomWallsCount: roomWallsInput.length,
  //   resizeDirection: resizeHandleDirection,
  //   targetPoint: `(${targetPoint.x.toFixed(1)},${targetPoint.y.toFixed(1)})`,
  //   timestamp: Date.now()
  // });
  // console.log('[calculateSplitWallExtrusion] 🔧 EXTRUSION: Creating extrusion for split wall movement', {
  //   draggedWall: `(${draggedWallSegmentProp.start.x.toFixed(1)},${draggedWallSegmentProp.start.y.toFixed(1)})->(${draggedWallSegmentProp.end.x.toFixed(1)},${draggedWallSegmentProp.end.y.toFixed(1)})`,
  //   splitWallTempId: (draggedWallSegmentProp as any).tempId,
  //   parentWallId: (draggedWallSegmentProp as any).parentWallId,
  //   parentRoomIds: (draggedWallSegmentProp as any).parentRoomIds,
  //   isSplitOrigin: (draggedWallSegmentProp as any).isSplitOrigin,
  //   isMainExtrusionSegment: (draggedWallSegmentProp as any).isMainExtrusionSegment,
  //   resizeDirection: resizeHandleDirection,
  //   targetPoint: `(${targetPoint.x.toFixed(1)},${targetPoint.y.toFixed(1)})`
  // });
  
  let newWallsWorkingCopy: Wall[] = JSON.parse(JSON.stringify(roomWallsInput));
  
  // Find the dragged wall in the working copy
  const findWallInWorkingCopy = (refWall: Wall, walls: Wall[]): Wall | undefined => {
    return walls.find(w => 
      (w.start.x === refWall.start.x && w.start.y === refWall.start.y && w.end.x === refWall.end.x && w.end.y === refWall.end.y) ||
      (w.start.x === refWall.end.x && w.start.y === refWall.end.y && w.end.x === refWall.start.x && w.end.y === refWall.start.y)
    );
  };
  
  const segmentBeingDragged = findWallInWorkingCopy(draggedWallSegmentProp, newWallsWorkingCopy);
  if (!segmentBeingDragged) {
    console.error('[calculateSplitWallExtrusion] Dragged wall segment not found');
    return roomWallsInput;
  }
  
  // Check if this split wall already has extrusion connectors (multiple resize scenario)
  // Use parentWallId and other stable identifiers instead of coordinate matching
  const parentWallId = (draggedWallSegmentProp as any).parentWallId;
  const splitWallTempId = (draggedWallSegmentProp as any).tempId;
  
  const existingConnectors = newWallsWorkingCopy.filter(wall => {
    if (!wall.isExtrusionConnector) return false;
    
    // Check if this connector belongs to the same parent wall system
    const wallParentId = (wall as any).parentWallId;
    const connectedToSplitWallId = (wall as any).connectedToSplitWallId;
    
    // Primary match: check if connector is directly connected to this split wall by ID
    if (connectedToSplitWallId === splitWallTempId) {
      return true;
    }
    
    // Secondary match: Match by parent wall ID (more stable than coordinates)
    if (parentWallId && wallParentId === parentWallId) {
      return true;
    }
    
    // Tertiary match: check if this connector has the same parent room IDs
    const wallParentRoomIds = (wall as any).parentRoomIds;
    const splitWallParentRoomIds = (draggedWallSegmentProp as any).parentRoomIds;
    
    if (wallParentRoomIds && splitWallParentRoomIds && 
        Array.isArray(wallParentRoomIds) && Array.isArray(splitWallParentRoomIds)) {
      const hasMatchingRooms = wallParentRoomIds.length === splitWallParentRoomIds.length &&
                               wallParentRoomIds.every(id => splitWallParentRoomIds.includes(id));
      if (hasMatchingRooms) {
        return true;
      }
    }
    
    // 🔧 ENHANCED: coordinate-based matching using ORIGINAL segment coordinates from draggedWallSegmentProp
    // Check if this connector connects to either endpoint of the split wall segment
    const tolerance = 0.1; // Increased tolerance for better reliability
    const originalStart = draggedWallSegmentProp.start;
    const originalEnd = draggedWallSegmentProp.end;
    
    const connectsToStart = (Math.abs(wall.start.x - originalStart.x) < tolerance && Math.abs(wall.start.y - originalStart.y) < tolerance) ||
                           (Math.abs(wall.end.x - originalStart.x) < tolerance && Math.abs(wall.end.y - originalStart.y) < tolerance);
                           
    const connectsToEnd = (Math.abs(wall.start.x - originalEnd.x) < tolerance && Math.abs(wall.start.y - originalEnd.y) < tolerance) ||
                         (Math.abs(wall.end.x - originalEnd.x) < tolerance && Math.abs(wall.end.y - originalEnd.y) < tolerance);
    
    return connectsToStart || connectsToEnd;
  });
  
  if (existingConnectors.length > 0) {
    console.log(`[calculateSplitWallExtrusion] 🔧 MULTIPLE RESIZE: Found ${existingConnectors.length} existing connectors - updating existing extrusion`, {
      splitWallId: splitWallTempId,
      parentWallId,
      existingConnectorIds: existingConnectors.map(c => (c as any).tempId || 'no-temp-id'),
      existingConnectorParentIds: existingConnectors.map(c => (c as any).parentWallId),
      existingConnectorSplitWallIds: existingConnectors.map(c => (c as any).connectedToSplitWallId),
      coordinates: existingConnectors.map(c => `(${c.start.x.toFixed(1)},${c.start.y.toFixed(1)})->(${c.end.x.toFixed(1)},${c.end.y.toFixed(1)})`),
      originalSegmentCoords: `(${draggedWallSegmentProp.start.x.toFixed(1)},${draggedWallSegmentProp.start.y.toFixed(1)})->(${draggedWallSegmentProp.end.x.toFixed(1)},${draggedWallSegmentProp.end.y.toFixed(1)})`,
      targetPoint: `(${targetPoint.x.toFixed(1)},${targetPoint.y.toFixed(1)})`,
      resizeDirection: resizeHandleDirection
    });
    const updatedWalls = updateExistingExtrusion(newWallsWorkingCopy, segmentBeingDragged, resizeHandleDirection, targetPoint, existingConnectors);
    
    // 🛡️ VALIDATE: Ensure no walls were lost during extrusion update
    const validation = validateWallSetIntegrity(roomWallsInput, updatedWalls, 'existing_extrusion_update');
    if (!validation.isValid) {
      console.error('🚨 EXTRUSION_UPDATE_VALIDATION_FAILED:', validation);
    }
    
    return updatedWalls;
  }
  
  const P1_orig = segmentBeingDragged.start;
  const P2_orig = segmentBeingDragged.end;
  let P1_new = { ...P1_orig };
  let P2_new = { ...P2_orig };
  
  // Move the split wall to the target position
  if (resizeHandleDirection === 'n' || resizeHandleDirection === 's') {
    P1_new.y = targetPoint.y;
    P2_new.y = targetPoint.y;
  } else if (resizeHandleDirection === 'w' || resizeHandleDirection === 'e') {
    P1_new.x = targetPoint.x;
    P2_new.x = targetPoint.x;
  }
  
  // 🔧 IMPROVED: Determine if this is an inward resize using resize handle direction and movement
  // For split walls, room center calculation can be misleading, so use directional logic instead
  const isResizingInward = (() => {
    // Calculate the actual movement vector
    const movementVector = {
      x: (P1_new.x + P2_new.x) / 2 - (P1_orig.x + P2_orig.x) / 2,
      y: (P1_new.y + P2_new.y) / 2 - (P1_orig.y + P2_orig.y) / 2
    };
    
    // For split walls, use a simpler approach based on resize handle direction
    // and whether the movement is significant enough to create an extrusion
    const movementMagnitude = Math.sqrt(movementVector.x * movementVector.x + movementVector.y * movementVector.y);
    
    // 🔧 FIXED: Lower threshold for small movements to avoid blocking connector creation
    // Only treat tiny movements (< 2 pixels) as inward to prevent accidental extrusions
    if (movementMagnitude < 2) {
      console.log('[calculateSplitWallExtrusion] 🔧 TINY MOVEMENT: Treating as inward resize due to tiny movement', {
        movementMagnitude,
        threshold: 2
      });
      return true;
    }
    
    // 🔧 FIXED: For split walls, default to creating extrusions for any significant movement
    // This restores the original behavior where split walls would create connector walls
    console.log('[calculateSplitWallExtrusion] 🔧 SIGNIFICANT MOVEMENT: Creating extrusion for split wall movement', {
      movementMagnitude,
      direction: resizeHandleDirection,
      willCreateExtrusion: true
    });
    return false;
  })();
  
  console.log(`[calculateSplitWallExtrusion] 🔧 RESIZE DIRECTION: ${isResizingInward ? 'INWARD' : 'OUTWARD'}`, {
    resizeHandle: resizeHandleDirection,
    originalPos: `(${P1_orig.x.toFixed(1)},${P1_orig.y.toFixed(1)})`,
    newPos: `(${P1_new.x.toFixed(1)},${P1_new.y.toFixed(1)})`,
    targetPos: `(${targetPoint.x.toFixed(1)},${targetPoint.y.toFixed(1)})`,
    movementVector: `(${((P1_new.x + P2_new.x) / 2 - (P1_orig.x + P2_orig.x) / 2).toFixed(1)},${((P1_new.y + P2_new.y) / 2 - (P1_orig.y + P2_orig.y) / 2).toFixed(1)})`
  });
  
  // 🔧 NEW: If resizing inward, use standard resize logic instead of creating extrusions
  if (isResizingInward) {
    console.log('[calculateSplitWallExtrusion] 🔧 INWARD RESIZE: Using standard resize logic to maintain room boundary');
    
    // For inward resize, we should use the standard resize logic that properly 
    // extends/shortens adjacent walls to maintain the room boundary.
    // This is handled by the main calculateResizedWalls function.
    // Return null to indicate that standard resize should be used instead.
    return null;
  } else {
    console.log('[calculateSplitWallExtrusion] 🔧 OUTWARD RESIZE: Creating extrusion connectors');
  }
  
  // 🔧 NEW: Find which endpoint is the split point (shared with another segment from the same parent wall)
  const findSplitPoint = (): Point | null => {
    const tolerance = 0.1;
    const parentWallId = (draggedWallSegmentProp as any).parentWallId;
    
    console.log(`[calculateSplitWallExtrusion] 🔧 SPLIT POINT DETECTION: Starting detection`, {
      draggedSegment: `(${P1_orig.x.toFixed(1)},${P1_orig.y.toFixed(1)})->(${P2_orig.x.toFixed(1)},${P2_orig.y.toFixed(1)})`,
      parentWallId,
      P1_orig: `(${P1_orig.x.toFixed(1)},${P1_orig.y.toFixed(1)})`,
      P2_orig: `(${P2_orig.x.toFixed(1)},${P2_orig.y.toFixed(1)})`,
      tolerance,
      totalWallsInWorkingCopy: newWallsWorkingCopy.length
    });
    
    if (!parentWallId) {
      console.log('[calculateSplitWallExtrusion] 🔧 SPLIT POINT DETECTION: No parentWallId found');
      return null;
    }
    
    // Find other segments from the same parent wall
    const siblingSegments = newWallsWorkingCopy.filter(wall => {
      const wallParentId = (wall as any).parentWallId;
      const isSibling = wallParentId === parentWallId && wall !== segmentBeingDragged;
      
      if (isSibling) {
        console.log(`[calculateSplitWallExtrusion] 🔧 SPLIT POINT DETECTION: Found sibling segment`, {
          siblingStart: `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})`,
          siblingEnd: `(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`,
          siblingParentId: wallParentId,
          draggedParentId: parentWallId
        });
      }
      
      return isSibling;
    });
    
    console.log(`[calculateSplitWallExtrusion] 🔧 SPLIT POINT DETECTION: Found ${siblingSegments.length} sibling segments`);
    
    if (siblingSegments.length === 0) {
      console.log('[calculateSplitWallExtrusion] 🔧 SPLIT POINT DETECTION: No sibling segments found');
      return null;
    }
    
    // Check which endpoint is shared with sibling segments
    for (const sibling of siblingSegments) {
      console.log(`[calculateSplitWallExtrusion] 🔧 SPLIT POINT DETECTION: Checking sibling (${sibling.start.x.toFixed(1)},${sibling.start.y.toFixed(1)})->(${sibling.end.x.toFixed(1)},${sibling.end.y.toFixed(1)})`);
      
      // Check if P1_orig is shared with sibling
      const p1MatchesSiblingStart = Math.abs(P1_orig.x - sibling.start.x) < tolerance && Math.abs(P1_orig.y - sibling.start.y) < tolerance;
      const p1MatchesSiblingEnd = Math.abs(P1_orig.x - sibling.end.x) < tolerance && Math.abs(P1_orig.y - sibling.end.y) < tolerance;
      
      console.log(`[calculateSplitWallExtrusion] 🔧 SPLIT POINT DETECTION: P1 matches - start: ${p1MatchesSiblingStart}, end: ${p1MatchesSiblingEnd}`);
      
      if (p1MatchesSiblingStart || p1MatchesSiblingEnd) {
        console.log(`[calculateSplitWallExtrusion] 🔧 SPLIT POINT DETECTION: P1 is the split point (${P1_orig.x.toFixed(1)},${P1_orig.y.toFixed(1)})`);
        return P1_orig; // P1 is the split point
      }
      
      // Check if P2_orig is shared with sibling
      const p2MatchesSiblingStart = Math.abs(P2_orig.x - sibling.start.x) < tolerance && Math.abs(P2_orig.y - sibling.start.y) < tolerance;
      const p2MatchesSiblingEnd = Math.abs(P2_orig.x - sibling.end.x) < tolerance && Math.abs(P2_orig.y - sibling.end.y) < tolerance;
      
      console.log(`[calculateSplitWallExtrusion] 🔧 SPLIT POINT DETECTION: P2 matches - start: ${p2MatchesSiblingStart}, end: ${p2MatchesSiblingEnd}`);
      
      if (p2MatchesSiblingStart || p2MatchesSiblingEnd) {
        console.log(`[calculateSplitWallExtrusion] 🔧 SPLIT POINT DETECTION: P2 is the split point (${P2_orig.x.toFixed(1)},${P2_orig.y.toFixed(1)})`);
        return P2_orig; // P2 is the split point
      }
    }
    
    console.log('[calculateSplitWallExtrusion] 🔧 SPLIT POINT DETECTION: No shared endpoint detected with sibling segments');
    return null;
  };
  
  // 🔧 NEW: Function to identify if an endpoint is an original room endpoint (should never use connectors)
  const isOriginalRoomEndpoint = (point: Point): boolean => {
    const tolerance = 0.1;
    
    // An endpoint is "original" if it's connected to walls that are NOT split segments from the same parent
    const connectedWalls = newWallsWorkingCopy.filter(wall => {
      if (wall === segmentBeingDragged) return false;
      
      const connectsAtStart = Math.abs(wall.start.x - point.x) < tolerance && Math.abs(wall.start.y - point.y) < tolerance;
      const connectsAtEnd = Math.abs(wall.end.x - point.x) < tolerance && Math.abs(wall.end.y - point.y) < tolerance;
      
      return connectsAtStart || connectsAtEnd;
    });
    
    console.log(`[calculateSplitWallExtrusion] 🔧 ORIGINAL ENDPOINT CHECK: Point (${point.x.toFixed(1)},${point.y.toFixed(1)})`, {
      connectedWallsCount: connectedWalls.length,
      connectedWalls: connectedWalls.map(w => ({
        coords: `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`,
        isSplitOrigin: (w as any).isSplitOrigin,
        parentWallId: (w as any).parentWallId,
        isExtrusionConnector: w.isExtrusionConnector
      }))
    });
    
    // If this point connects to:
    // 1. Regular walls (not split segments)
    // 2. Or split segments from DIFFERENT parent walls
    // Then it's an original room endpoint
    
    const parentWallId = (draggedWallSegmentProp as any).parentWallId;
    
    for (const connectedWall of connectedWalls) {
      // Skip extrusion connectors - they don't count as original walls
      if (connectedWall.isExtrusionConnector) continue;
      
      const connectedWallParentId = (connectedWall as any).parentWallId;
      
      // If connected to a regular wall (no parent ID), this is an original endpoint
      if (!connectedWallParentId) {
        console.log(`[calculateSplitWallExtrusion] 🔧 ORIGINAL ENDPOINT: Connected to regular wall - this is an original room endpoint`);
        return true;
      }
      
      // If connected to a split segment from a DIFFERENT parent wall, this is an original endpoint
      if (connectedWallParentId !== parentWallId) {
        console.log(`[calculateSplitWallExtrusion] 🔧 ORIGINAL ENDPOINT: Connected to different parent wall (${connectedWallParentId} vs ${parentWallId}) - this is an original room endpoint`);
        return true;
      }
    }
    
    // If we get here, this point only connects to split segments from the same parent wall
    // This means it's likely a split point, not an original endpoint
    console.log(`[calculateSplitWallExtrusion] 🔧 SPLIT ENDPOINT: Only connects to segments from same parent wall - this is a split point`);
    return false;
  };
  
  const splitPoint = findSplitPoint();
  
  // 🔧 EXISTING LOGIC: For outward resize, create extrusion connectors
  segmentBeingDragged.start = P1_new;
  segmentBeingDragged.end = P2_new;
  
  // 🔧 ENHANCED: Create extrusion connector walls ONLY for split points, NEVER for original room endpoints
  const extrusionWalls: Wall[] = [];
  const parentRoomIds = (draggedWallSegmentProp as any).parentRoomIds;
  
  // 🎯 ROOT CAUSE FIX: Use centralized connector duplication prevention with enhanced search scope
  const checkForExistingConnector = (startPoint: Point, endPoint: Point): boolean => {
    const connectorData = { start: startPoint, end: endPoint };
    
    // ⚡ CRITICAL FIX: Expand search scope to include all available walls for multi-operation connector detection
    const wallsToSearch = allAvailableWalls && allAvailableWalls.length > 0 
      ? [...newWallsWorkingCopy, ...allAvailableWalls]
      : newWallsWorkingCopy;
      
    const existenceCheck = checkConnectorExists(connectorData, wallsToSearch, 0.1);
    
    console.log('🔍 [calculateSplitWallExtrusion] CONNECTOR_EXISTENCE_CHECK', {
      searchScope: allAvailableWalls ? `${newWallsWorkingCopy.length} working + ${allAvailableWalls.length} additional = ${wallsToSearch.length} total` : `${newWallsWorkingCopy.length} working only`,
      connectorExists: existenceCheck.exists,
      proposedConnector: `(${startPoint.x.toFixed(1)},${startPoint.y.toFixed(1)})->(${endPoint.x.toFixed(1)},${endPoint.y.toFixed(1)})`,
      existingConnector: existenceCheck.exists && existenceCheck.existingConnector ? 
        `(${existenceCheck.existingConnector.start.x.toFixed(1)},${existenceCheck.existingConnector.start.y.toFixed(1)})->(${existenceCheck.existingConnector.end.x.toFixed(1)},${existenceCheck.existingConnector.end.y.toFixed(1)})` : null,
      reason: existenceCheck.reason
    });
    
    return existenceCheck.exists;
  };
  
  // Check if the connector would be too short to be meaningful
  const isConnectorTooShort = (startPoint: Point, endPoint: Point): boolean => {
    const length = Math.sqrt(Math.pow(endPoint.x - startPoint.x, 2) + Math.pow(endPoint.y - startPoint.y, 2));
    return length < 0.1; // Filter out very short connectors
  };
  
  // 🔧 CRITICAL FIX: Apply the "original room endpoints never use connectors" rule
  console.log('🔧 [calculateSplitWallExtrusion] CONNECTOR_CREATION_LOGIC_REACHED', {
    splitPointFound: splitPoint ? `(${splitPoint.x.toFixed(1)},${splitPoint.y.toFixed(1)})` : 'Not found',
    P1_isOriginal: isOriginalRoomEndpoint(P1_orig),
    P2_isOriginal: isOriginalRoomEndpoint(P2_orig),
    willCreateConnectorsOnlyForSplitPoint: true,
    draggedWallCoords: `(${draggedWallSegmentProp.start.x.toFixed(1)},${draggedWallSegmentProp.start.y.toFixed(1)})->(${draggedWallSegmentProp.end.x.toFixed(1)},${draggedWallSegmentProp.end.y.toFixed(1)})`
  });
  
  console.log(`[calculateSplitWallExtrusion] 🔧 ENDPOINT ANALYSIS: Determining which endpoints can use connectors`, {
    splitPoint: splitPoint ? `(${splitPoint.x.toFixed(1)},${splitPoint.y.toFixed(1)})` : 'Not found',
    P1_isOriginal: isOriginalRoomEndpoint(P1_orig),
    P2_isOriginal: isOriginalRoomEndpoint(P2_orig)
  });
  
  // Only create connectors for split points, never for original room endpoints
  if (splitPoint) {
    const splitPointNew = (Math.abs(splitPoint.x - P1_orig.x) < 0.1 && Math.abs(splitPoint.y - P1_orig.y) < 0.1) ? P1_new : P2_new;
    
    // Double-check: even if we identified a split point, make sure it's not an original room endpoint
    if (!isOriginalRoomEndpoint(splitPoint)) {
    if ((splitPoint.x !== splitPointNew.x || splitPoint.y !== splitPointNew.y) && 
        !checkForExistingConnector(splitPoint, splitPointNew) && 
        !isConnectorTooShort(splitPoint, splitPointNew)) {
      
      // 🎯 ROOT CAUSE FIX: Apply precise grid snapping for coordinate accuracy
      // Use 0.1 precision instead of GRID_SIZE (30) to prevent coordinate drift
      const precisionSplitPoint = snapPointToGrid(splitPoint, 0.1);
      const precisionSplitPointNew = snapPointToGrid(splitPointNew, 0.1);
      
      const safeConnectorResult = createSafeConnector({
        start: precisionSplitPoint,
        end: precisionSplitPointNew,
        type: segmentBeingDragged.type || 'interior',
        floor: segmentBeingDragged.floor,
        parentWallId: parentWallId,
        parentRoomIds: parentRoomIds,
        thickness: WALL_THICKNESS,
        // 🔧 CRITICAL FIX: Add connectedToSplitWallId for proper relationship tracking
        connectedToSplitWallId: splitWallTempId,
        // 🏠 CRITICAL FIX: Assign canonical ownership fields from the parent wall
        originatingRoomId: (draggedWallSegmentProp as any).originatingRoomId,
        assignedRoomIds: (draggedWallSegmentProp as any).assignedRoomIds ? [...(draggedWallSegmentProp as any).assignedRoomIds] : undefined
      } as any, newWallsWorkingCopy, 0.1);
      
      if (!safeConnectorResult.wasCreated) {
        console.log('🚫 EXTRUSION_CONNECTOR_SKIPPED:', safeConnectorResult.reason);
        // Don't add this connector, but continue processing
      } else {
      
      const newExtrusionConnector = safeConnectorResult.connector!;
      
      // 🔍 COMPREHENSIVE LOGGING: Track wall creation with ownership details
      console.log('🔍 [calculateSplitWallExtrusion] EXTRUSION_CONNECTOR_CREATED:', {
        wallCoords: `(${splitPoint.x.toFixed(1)},${splitPoint.y.toFixed(1)})->(${splitPointNew.x.toFixed(1)},${splitPointNew.y.toFixed(1)})`,
        parentWallOwnership: {
          originatingRoomId: (draggedWallSegmentProp as any).originatingRoomId || 'NONE',
          assignedRoomIds: (draggedWallSegmentProp as any).assignedRoomIds || 'NONE'
        },
        newWallOwnership: {
          originatingRoomId: newExtrusionConnector.originatingRoomId || 'NONE',
          assignedRoomIds: newExtrusionConnector.assignedRoomIds || 'NONE'
        },
        hasOwnership: !!(newExtrusionConnector.originatingRoomId && newExtrusionConnector.assignedRoomIds),
        timestamp: Date.now(),
        stackTrace: new Error().stack?.split('\n').slice(1, 4).join(' → ')
      });
      
      extrusionWalls.push(newExtrusionConnector);
      
      console.log(`[calculateSplitWallExtrusion] 🔧 SPLIT POINT CONNECTOR CREATED: From split point (${splitPoint.x.toFixed(1)},${splitPoint.y.toFixed(1)}) to (${splitPointNew.x.toFixed(1)},${splitPointNew.y.toFixed(1)})`);
      }
    } else {
      console.log(`[calculateSplitWallExtrusion] 🔧 SPLIT POINT CONNECTOR SKIPPED:`, {
        reason: splitPoint.x === splitPointNew.x && splitPoint.y === splitPointNew.y ? 'No movement' :
                checkForExistingConnector(splitPoint, splitPointNew) ? 'Existing connector found' :
                isConnectorTooShort(splitPoint, splitPointNew) ? 'Connector too short' : 'Unknown',
        splitPoint: `(${splitPoint.x.toFixed(1)},${splitPoint.y.toFixed(1)})`,
        splitPointNew: `(${splitPointNew.x.toFixed(1)},${splitPointNew.y.toFixed(1)})`
      });
    }
  } else {
      console.log(`[calculateSplitWallExtrusion] 🔧 ORIGINAL ENDPOINT PROTECTION: Split point is actually an original room endpoint - no connector created`);
    }
  }
  
  // 🔧 CRITICAL: For ALL endpoints (both split and original), ensure connected walls extend/retract naturally
  // This is the key fix - original room endpoints should ALWAYS allow wall extension/retraction
  const handleEndpointExtension = (originalEndpoint: Point, newEndpoint: Point, endpointType: string) => {
    const tolerance = 0.1;
    
    // Find walls connected to this endpoint (excluding the segment being dragged and extrusion connectors)
    const connectedWalls = newWallsWorkingCopy.filter(wall => {
      if (wall === segmentBeingDragged) return false;
      if (wall.isExtrusionConnector) return false;
      
      const connectsAtStart = Math.abs(wall.start.x - originalEndpoint.x) < tolerance && Math.abs(wall.start.y - originalEndpoint.y) < tolerance;
      const connectsAtEnd = Math.abs(wall.end.x - originalEndpoint.x) < tolerance && Math.abs(wall.end.y - originalEndpoint.y) < tolerance;
      
      return connectsAtStart || connectsAtEnd;
    });
    
    console.log(`[calculateSplitWallExtrusion] 🔧 ${endpointType} ENDPOINT EXTENSION: Found ${connectedWalls.length} walls to extend/retract`, {
      originalEndpoint: `(${originalEndpoint.x.toFixed(1)},${originalEndpoint.y.toFixed(1)})`,
      newEndpoint: `(${newEndpoint.x.toFixed(1)},${newEndpoint.y.toFixed(1)})`,
      connectedWalls: connectedWalls.map(w => `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`)
    });
    
    // Extend/retract each connected wall to maintain room shape
    connectedWalls.forEach(wall => {
      const wallConnectsAtStart = Math.abs(wall.start.x - originalEndpoint.x) < tolerance && Math.abs(wall.start.y - originalEndpoint.y) < tolerance;
      const wallConnectsAtEnd = Math.abs(wall.end.x - originalEndpoint.x) < tolerance && Math.abs(wall.end.y - originalEndpoint.y) < tolerance;
      
      if (wallConnectsAtStart) {
        // Calculate proper perpendicular extension for the wall
        const wallVector = { x: wall.end.x - wall.start.x, y: wall.end.y - wall.start.y };
        const wallLength = Math.sqrt(wallVector.x * wallVector.x + wallVector.y * wallVector.y);
        
        if (wallLength > 0) {
          // Normalize the wall direction
          const wallDirection = { x: wallVector.x / wallLength, y: wallVector.y / wallLength };
          
          // Calculate movement vector from original to new endpoint
          const movementVector = { x: newEndpoint.x - originalEndpoint.x, y: newEndpoint.y - originalEndpoint.y };
          
          // Project movement onto wall direction to get proper extension
          const projectedLength = movementVector.x * wallDirection.x + movementVector.y * wallDirection.y;
          
          // Move the start point along the wall's direction by the projected amount
          wall.start.x = originalEndpoint.x + projectedLength * wallDirection.x;
          wall.start.y = originalEndpoint.y + projectedLength * wallDirection.y;
          
          console.log(`[calculateSplitWallExtrusion] 🔧 PROPER WALL EXTENSION START: Wall extended by ${projectedLength.toFixed(2)} units along its direction to (${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})`);
        } else {
          // Fallback for zero-length walls
          wall.start.x = newEndpoint.x;
          wall.start.y = newEndpoint.y;
        }
      } else if (wallConnectsAtEnd) {
        // Calculate proper perpendicular extension for the wall
        const wallVector = { x: wall.start.x - wall.end.x, y: wall.start.y - wall.end.y };
        const wallLength = Math.sqrt(wallVector.x * wallVector.x + wallVector.y * wallVector.y);
        
        if (wallLength > 0) {
          // Normalize the wall direction (from end to start)
          const wallDirection = { x: wallVector.x / wallLength, y: wallVector.y / wallLength };
          
          // Calculate movement vector from original to new endpoint
          const movementVector = { x: newEndpoint.x - originalEndpoint.x, y: newEndpoint.y - originalEndpoint.y };
          
          // Project movement onto wall direction to get proper extension
          const projectedLength = movementVector.x * wallDirection.x + movementVector.y * wallDirection.y;
          
          // Move the end point along the wall's direction by the projected amount
          wall.end.x = originalEndpoint.x + projectedLength * wallDirection.x;
          wall.end.y = originalEndpoint.y + projectedLength * wallDirection.y;
          
          console.log(`[calculateSplitWallExtrusion] 🔧 PROPER WALL EXTENSION END: Wall extended by ${projectedLength.toFixed(2)} units along its direction to (${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`);
        } else {
          // Fallback for zero-length walls
          wall.end.x = newEndpoint.x;
          wall.end.y = newEndpoint.y;
        }
      }
    });
  };
  
  // Apply extension logic ONLY to the endpoint that is NOT the split point
  // The split point should stay fixed with connectors, while the original room endpoint should extend walls
  if (splitPoint) {
    const splitPointIsP1 = Math.abs(splitPoint.x - P1_orig.x) < 0.1 && Math.abs(splitPoint.y - P1_orig.y) < 0.1;
    const splitPointIsP2 = Math.abs(splitPoint.x - P2_orig.x) < 0.1 && Math.abs(splitPoint.y - P2_orig.y) < 0.1;
    
    console.log(`[calculateSplitWallExtrusion] 🔧 SELECTIVE ENDPOINT EXTENSION: splitPointIsP1=${splitPointIsP1}, splitPointIsP2=${splitPointIsP2}`);
  
  if (splitPointIsP1) {
    // P1 is the split point (stays fixed with connectors), P2 is the original endpoint (extends walls)
    handleEndpointExtension(P2_orig, P2_new, "P2");
    console.log(`[calculateSplitWallExtrusion] 🔧 P1 is split point (fixed), P2 is original endpoint (extends walls)`);
  } else if (splitPointIsP2) {
    // P2 is the split point (stays fixed with connectors), P1 is the original endpoint (extends walls)
    handleEndpointExtension(P1_orig, P1_new, "P1");
    console.log(`[calculateSplitWallExtrusion] 🔧 P2 is split point (fixed), P1 is original endpoint (extends walls)`);
  } else {
    // Fallback: if we can't determine which is the split point, apply extension to both (old behavior)
    handleEndpointExtension(P1_orig, P1_new, "P1");
    handleEndpointExtension(P2_orig, P2_new, "P2");
    console.log(`[calculateSplitWallExtrusion] 🔧 FALLBACK: Could not determine split point, applying extension to both endpoints`);
  }
} else {
  // No split point detected, apply extension to both endpoints (old behavior)
  handleEndpointExtension(P1_orig, P1_new, "P1");
  handleEndpointExtension(P2_orig, P2_new, "P2");
  console.log(`[calculateSplitWallExtrusion] 🔧 No split point detected, applying extension to both endpoints`);
}

// 🔧 CRITICAL FIX: Mark the split wall as a main extrusion segment
// This ensures future resize operations can find it
(segmentBeingDragged as any).isMainExtrusionSegment = true;

console.log('🔧 [BAY_WINDOW_FIX] Split wall marked as main extrusion segment:', {
  splitWallId: (segmentBeingDragged as any).tempId || 'no-tempId',
  splitWallCoords: `(${segmentBeingDragged.start.x.toFixed(1)},${segmentBeingDragged.start.y.toFixed(1)})->(${segmentBeingDragged.end.x.toFixed(1)},${segmentBeingDragged.end.y.toFixed(1)})`,
  isMainExtrusionSegment: (segmentBeingDragged as any).isMainExtrusionSegment,
  isSplitOrigin: (segmentBeingDragged as any).isSplitOrigin,
  parentWallId: (segmentBeingDragged as any).parentWallId
});
  
  // 🔍 COMPREHENSIVE LOGGING: Track extrusion walls being added to working copy
  console.log('🔍 [calculateSplitWallExtrusion] ADDING_EXTRUSION_WALLS_TO_WORKING_COPY:', {
    extrusionWallsCount: extrusionWalls.length,
    extrusionWallsDetails: extrusionWalls.map(wall => ({
      coords: `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})->(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`,
      isExtrusionConnector: wall.isExtrusionConnector,
      isMainExtrusionSegment: (wall as any).isMainExtrusionSegment,
      hasOwnership: !!(wall.originatingRoomId && wall.assignedRoomIds),
      originatingRoomId: wall.originatingRoomId || 'NONE',
      assignedRoomIds: wall.assignedRoomIds || 'NONE'
    })),
    workingCopyCountBefore: newWallsWorkingCopy.length
  });
  
  // Add the extrusion walls to the working copy
  newWallsWorkingCopy.push(...extrusionWalls);
  
  // 🔍 COMPREHENSIVE LOGGING: Track final walls being returned
  console.log('🔍 [calculateSplitWallExtrusion] RETURNING_FINAL_WALLS:', {
    totalWalls: newWallsWorkingCopy.length,
    extrusionWallsAdded: extrusionWalls.length,
    finalWallsOwnershipSummary: {
      totalWalls: newWallsWorkingCopy.length,
      ownedWalls: newWallsWorkingCopy.filter(w => w.originatingRoomId && w.assignedRoomIds).length,
      unownedWalls: newWallsWorkingCopy.filter(w => !w.originatingRoomId || !w.assignedRoomIds).length
    },
    unownedWallDetails: newWallsWorkingCopy
      .filter(w => !w.originatingRoomId || !w.assignedRoomIds)
      .map(w => `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`)
  });
  
  console.log('[calculateSplitWallExtrusion] ✅ EXTRUSION: Returning updated walls', {
    totalWalls: newWallsWorkingCopy.length,
    extrusionWallsAdded: extrusionWalls.length
  });
  
  return newWallsWorkingCopy;
};



// Helper function to move only the main extrusion segment and its connectors, keeping main room stable
const moveMainExtrusionSegmentOnly = (
  roomWalls: Wall[],
  mainSegment: Wall,
  resizeHandleDirection: string,
  targetPoint: Point
): Wall[] => {
  console.log('[moveMainExtrusionSegmentOnly] Moving only extrusion segment and connectors, keeping main room stable', {
    inputWallCount: roomWalls.length,
    mainSegmentId: (mainSegment as any).tempId || 'no-tempId',
    mainSegmentCoords: `(${mainSegment.start.x.toFixed(1)},${mainSegment.start.y.toFixed(1)})->(${mainSegment.end.x.toFixed(1)},${mainSegment.end.y.toFixed(1)})`
  });
  
  // Make a working copy of all walls
  const updatedWalls = roomWalls.map(wall => ({ 
    ...wall, 
    start: { ...wall.start }, 
    end: { ...wall.end } 
  }));
  
  // Find the main segment by tempId or coordinate matching
  const mainSegmentIndex = updatedWalls.findIndex(wall => {
    // Try tempId matching first (most reliable)
    if ((wall as any).tempId && (mainSegment as any).tempId && (wall as any).tempId === (mainSegment as any).tempId) {
      return true;
    }
    
    // Fallback to coordinate matching with tolerance
    const tolerance = 0.01;
    const coordsMatch = (
      Math.abs(wall.start.x - mainSegment.start.x) < tolerance && 
      Math.abs(wall.start.y - mainSegment.start.y) < tolerance &&
      Math.abs(wall.end.x - mainSegment.end.x) < tolerance && 
      Math.abs(wall.end.y - mainSegment.end.y) < tolerance
    );
    
    const coordsMatchReversed = (
      Math.abs(wall.start.x - mainSegment.end.x) < tolerance && 
      Math.abs(wall.start.y - mainSegment.end.y) < tolerance &&
      Math.abs(wall.end.x - mainSegment.start.x) < tolerance && 
      Math.abs(wall.end.y - mainSegment.start.y) < tolerance
    );
    
    return coordsMatch || coordsMatchReversed;
  });
  
  if (mainSegmentIndex === -1) {
    console.error('[moveMainExtrusionSegmentOnly] Main segment not found in room walls');
    return roomWalls; // Return original if not found
  }
  
  const segmentToMove = updatedWalls[mainSegmentIndex];
  const originalStart = { ...segmentToMove.start };
  const originalEnd = { ...segmentToMove.end };
  
  // Move only the main segment to target position
  if (resizeHandleDirection === 'n' || resizeHandleDirection === 's') {
    // Moving vertically - set both endpoints to target Y, keep X coordinates
    segmentToMove.start.y = targetPoint.y;
    segmentToMove.end.y = targetPoint.y;
  } else if (resizeHandleDirection === 'w' || resizeHandleDirection === 'e') {
    // Moving horizontally - set both endpoints to target X, keep Y coordinates
    segmentToMove.start.x = targetPoint.x;
    segmentToMove.end.x = targetPoint.x;
  }
  
  // Update ONLY the extrusion connectors that connect to this main segment
  // Leave all room walls completely unchanged
  let connectorsUpdated = 0;
  updatedWalls.forEach(wall => {
    // Only update extrusion connectors, leave room walls untouched
    if (wall.isExtrusionConnector) {
      const tolerance = 0.1;
      let wasUpdated = false;
      
      // Check if this connector connects to the original start point
      if (Math.abs(wall.start.x - originalStart.x) < tolerance && Math.abs(wall.start.y - originalStart.y) < tolerance) {
        wall.start = { ...segmentToMove.start };
        wasUpdated = true;
      } else if (Math.abs(wall.end.x - originalStart.x) < tolerance && Math.abs(wall.end.y - originalStart.y) < tolerance) {
        wall.end = { ...segmentToMove.start };
        wasUpdated = true;
      }
      
      // Check if this connector connects to the original end point
      if (Math.abs(wall.start.x - originalEnd.x) < tolerance && Math.abs(wall.start.y - originalEnd.y) < tolerance) {
        wall.start = { ...segmentToMove.end };
        wasUpdated = true;
      } else if (Math.abs(wall.end.x - originalEnd.x) < tolerance && Math.abs(wall.end.y - originalEnd.y) < tolerance) {
        wall.end = { ...segmentToMove.end };
        wasUpdated = true;
      }
      
      if (wasUpdated) {
        connectorsUpdated++;
        console.log(`[moveMainExtrusionSegmentOnly] Updated connector: (${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)}) -> (${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`);
      }
    }
  });
  
  // Keep all walls as they are without additional cross-directional connectors
  const finalWalls = updatedWalls;
  
  console.log(`[moveMainExtrusionSegmentOnly] Moved main segment to: (${segmentToMove.start.x.toFixed(1)},${segmentToMove.start.y.toFixed(1)}) -> (${segmentToMove.end.x.toFixed(1)},${segmentToMove.end.y.toFixed(1)})`);
  console.log(`[moveMainExtrusionSegmentOnly] Updated ${connectorsUpdated} existing connectors`);
  
  // 🔧 FIX: Return ALL walls to maintain room structure integrity
  // The duplication prevention is now handled by calculateSplitWallExtrusion returning only new extrusion components
  const roomWallsCount = finalWalls.filter(w => !w.isMainExtrusionSegment && !w.isExtrusionConnector).length;
  const extrusionWallsCount = finalWalls.length - roomWallsCount;
  
  console.log(`[moveMainExtrusionSegmentOnly] ✅ ENHANCED: Returning ${finalWalls.length} walls (${roomWallsCount} room walls + ${extrusionWallsCount} extrusion walls)`);
  
  // Return ALL walls so room structure remains intact
  return finalWalls;
};

/**
 * 🔧 ENHANCED: Helper function to update existing extrusion when resizing multiple times
 * 
 * KEY IMPROVEMENTS TO FIX CONNECTOR WALL ADJUSTMENT ISSUE:
 * 1. Enhanced connector identification logic to properly find all related connectors
 * 2. Added handleConnectorWallUpdates() to update existing connector walls in place
 * 3. Improved endpoint extension logic to handle both original endpoints and connectors
 * 4. Fixed final wall assembly to keep all walls including updated connectors
 * 
 * This fixes the issue where connector walls weren't adjusting properly on subsequent
 * resizes of split wall segments.
 */
const updateExistingExtrusion = (
  walls: Wall[],
  splitWall: Wall,
  resizeHandleDirection: string,
  targetPoint: Point,
  existingConnectors: Wall[]
): Wall[] => {
  console.log('[updateExistingExtrusion] 🔧 ENHANCED: Updating existing extrusion for multiple resize with improved connector handling');
  
  // 🔧 FIX: Store original split wall coordinates before moving it
  const originalSplitStart = { ...splitWall.start };
  const originalSplitEnd = { ...splitWall.end };
  
  // 🔧 NEW: Find which endpoint is the split point (shared with another segment from the same parent wall)
  const findSplitPoint = (): Point | null => {
    const tolerance = 0.1;
    const parentWallId = (splitWall as any).parentWallId;
    
    console.log(`[updateExistingExtrusion] 🔧 SPLIT POINT DETECTION: Starting detection`, {
      parentWallId,
      P1_orig: `(${originalSplitStart.x.toFixed(1)},${originalSplitStart.y.toFixed(1)})`,
      P2_orig: `(${originalSplitEnd.x.toFixed(1)},${originalSplitEnd.y.toFixed(1)})`,
      tolerance
    });
    
    if (!parentWallId) {
      console.log('[updateExistingExtrusion] 🔧 SPLIT POINT DETECTION: No parentWallId found');
      return null;
    }
    
    // Find other segments from the same parent wall
    const siblingSegments = walls.filter(wall => {
      const wallParentId = (wall as any).parentWallId;
      const isSibling = wallParentId === parentWallId && wall !== splitWall;
      
      if (isSibling) {
        console.log(`[updateExistingExtrusion] 🔧 SPLIT POINT DETECTION: Found sibling segment`, {
          siblingStart: `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})`,
          siblingEnd: `(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`,
          siblingParentId: wallParentId
        });
      }
      
      return isSibling;
    });
    
    console.log(`[updateExistingExtrusion] 🔧 SPLIT POINT DETECTION: Found ${siblingSegments.length} sibling segments`);
    
    if (siblingSegments.length === 0) {
      console.log('[updateExistingExtrusion] 🔧 SPLIT POINT DETECTION: No sibling segments found');
      return null;
    }
    
    // Check which endpoint is shared with sibling segments
    for (const sibling of siblingSegments) {
      console.log(`[updateExistingExtrusion] 🔧 SPLIT POINT DETECTION: Checking sibling (${sibling.start.x.toFixed(1)},${sibling.start.y.toFixed(1)})->(${sibling.end.x.toFixed(1)},${sibling.end.y.toFixed(1)})`);
      
      // Check if originalSplitStart is shared with sibling
      const p1MatchesSiblingStart = Math.abs(originalSplitStart.x - sibling.start.x) < tolerance && Math.abs(originalSplitStart.y - sibling.start.y) < tolerance;
      const p1MatchesSiblingEnd = Math.abs(originalSplitStart.x - sibling.end.x) < tolerance && Math.abs(originalSplitStart.y - sibling.end.y) < tolerance;
      
      console.log(`[updateExistingExtrusion] 🔧 SPLIT POINT DETECTION: P1 matches - start: ${p1MatchesSiblingStart}, end: ${p1MatchesSiblingEnd}`);
      
      if (p1MatchesSiblingStart || p1MatchesSiblingEnd) {
        console.log(`[updateExistingExtrusion] 🔧 SPLIT POINT DETECTION: P1 is the split point (${originalSplitStart.x.toFixed(1)},${originalSplitStart.y.toFixed(1)})`);
        return originalSplitStart; // P1 is the split point
      }
      
      // Check if originalSplitEnd is shared with sibling
      const p2MatchesSiblingStart = Math.abs(originalSplitEnd.x - sibling.start.x) < tolerance && Math.abs(originalSplitEnd.y - sibling.start.y) < tolerance;
      const p2MatchesSiblingEnd = Math.abs(originalSplitEnd.x - sibling.end.x) < tolerance && Math.abs(originalSplitEnd.y - sibling.end.y) < tolerance;
      
      console.log(`[updateExistingExtrusion] 🔧 SPLIT POINT DETECTION: P2 matches - start: ${p2MatchesSiblingStart}, end: ${p2MatchesSiblingEnd}`);
      
      if (p2MatchesSiblingStart || p2MatchesSiblingEnd) {
        console.log(`[updateExistingExtrusion] 🔧 SPLIT POINT DETECTION: P2 is the split point (${originalSplitEnd.x.toFixed(1)},${originalSplitEnd.y.toFixed(1)})`);
        return originalSplitEnd; // P2 is the split point
      }
    }
    
    console.log('[updateExistingExtrusion] 🔧 SPLIT POINT DETECTION: No shared endpoint detected with sibling segments');
    return null;
  };
  
  // 🔧 NEW: Function to identify if an endpoint is an original room endpoint (should never use connectors)
  const isOriginalRoomEndpoint = (point: Point): boolean => {
    const tolerance = 0.1;
    
    // An endpoint is "original" if it's connected to walls that are NOT split segments from the same parent
    const connectedWalls = walls.filter(wall => {
      if (wall === splitWall) return false;
      
      const connectsAtStart = Math.abs(wall.start.x - point.x) < tolerance && Math.abs(wall.start.y - point.y) < tolerance;
      const connectsAtEnd = Math.abs(wall.end.x - point.x) < tolerance && Math.abs(wall.end.y - point.y) < tolerance;
      
      return connectsAtStart || connectsAtEnd;
    });
    
    console.log(`[updateExistingExtrusion] 🔧 ORIGINAL ENDPOINT CHECK: Point (${point.x.toFixed(1)},${point.y.toFixed(1)})`, {
      connectedWallsCount: connectedWalls.length,
      connectedWalls: connectedWalls.map(w => ({
        coords: `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`,
        isSplitOrigin: (w as any).isSplitOrigin,
        parentWallId: (w as any).parentWallId,
        isExtrusionConnector: w.isExtrusionConnector
      }))
    });
    
    const parentWallId = (splitWall as any).parentWallId;
    
    for (const connectedWall of connectedWalls) {
      // Skip extrusion connectors - they don't count as original walls
      if (connectedWall.isExtrusionConnector) continue;
      
      const connectedWallParentId = (connectedWall as any).parentWallId;
      
      // If connected to a regular wall (no parent ID), this is an original endpoint
      if (!connectedWallParentId) {
        console.log(`[updateExistingExtrusion] 🔧 ORIGINAL ENDPOINT: Connected to regular wall - this is an original room endpoint`);
        return true;
      }
      
      // If connected to a split segment from a DIFFERENT parent wall, this is an original endpoint
      if (connectedWallParentId !== parentWallId) {
        console.log(`[updateExistingExtrusion] 🔧 ORIGINAL ENDPOINT: Connected to different parent wall (${connectedWallParentId} vs ${parentWallId}) - this is an original room endpoint`);
        return true;
      }
    }
    
    console.log(`[updateExistingExtrusion] 🔧 SPLIT ENDPOINT: Only connects to segments from same parent wall - this is a split point`);
    return false;
  };
  
  const splitPoint = findSplitPoint();
  
  // Move the split wall to new position
  let P1_new = { ...splitWall.start };
  let P2_new = { ...splitWall.end };
  
  if (resizeHandleDirection === 'n' || resizeHandleDirection === 's') {
    P1_new.y = targetPoint.y;
    P2_new.y = targetPoint.y;
  } else if (resizeHandleDirection === 'w' || resizeHandleDirection === 'e') {
    P1_new.x = targetPoint.x;
    P2_new.x = targetPoint.x;
  }
  
  splitWall.start = P1_new;
  splitWall.end = P2_new;
  
  // 🔧 NEW: Create updated connectors only for split points, never for original room endpoints
  const updatedConnectors: Wall[] = [];
  const parentWallId = (splitWall as any).parentWallId;
  const parentRoomIds = (splitWall as any).parentRoomIds;
  const splitWallTempId = (splitWall as any).tempId;
  
  // Get existing extrusion group ID if available, otherwise create new one
  const existingGroupId = existingConnectors.length > 0 ? 
    (existingConnectors[0] as any).extrusionGroupId : 
    `${parentWallId}-extrusion-${Date.now()}`;
  
  // Check if the updated connector would be too short to be meaningful
  const isUpdatedConnectorTooShort = (startPoint: Point, endPoint: Point): boolean => {
    const length = Math.sqrt(Math.pow(endPoint.x - startPoint.x, 2) + Math.pow(endPoint.y - startPoint.y, 2));
    return length < 0.1; // Filter out very short connectors
  };
  
  // 🔧 CRITICAL FIX: Apply the "original room endpoints never use connectors" rule
  console.log(`[updateExistingExtrusion] 🔧 ENDPOINT ANALYSIS: Determining which endpoints can use connectors`, {
    splitPoint: splitPoint ? `(${splitPoint.x.toFixed(1)},${splitPoint.y.toFixed(1)})` : 'Not found',
    P1_isOriginal: isOriginalRoomEndpoint(originalSplitStart),
    P2_isOriginal: isOriginalRoomEndpoint(originalSplitEnd)
  });

  // Only create connectors for split points, never for original room endpoints
  if (splitPoint) {
    const splitPointNew = (Math.abs(splitPoint.x - originalSplitStart.x) < 0.1 && Math.abs(splitPoint.y - originalSplitStart.y) < 0.1) ? P1_new : P2_new;
    
    // Double-check: even if we identified a split point, make sure it's not an original room endpoint
    if (!isOriginalRoomEndpoint(splitPoint)) {
    if (!isUpdatedConnectorTooShort(splitPoint, splitPointNew)) {
      // 🎯 ROOT CAUSE FIX: Apply precise grid snapping for coordinate accuracy
      // Use 0.1 precision instead of GRID_SIZE (30) to prevent coordinate drift
      const precisionSplitPoint = snapPointToGrid(splitPoint, 0.1);
      const precisionSplitPointNew = snapPointToGrid(splitPointNew, 0.1);
      
      const safeConnectorResult = createSafeConnector({
        start: precisionSplitPoint,
        end: precisionSplitPointNew,
        type: splitWall.type || 'interior',
        floor: splitWall.floor,
        parentWallId: parentWallId,
        parentRoomIds: parentRoomIds,
        thickness: WALL_THICKNESS,
        // 🏠 CRITICAL FIX: Assign canonical ownership fields from the split wall
        originatingRoomId: (splitWall as any).originatingRoomId,
        assignedRoomIds: (splitWall as any).assignedRoomIds ? [...(splitWall as any).assignedRoomIds] : undefined
      } as any, walls, 0.1);
      
      if (!safeConnectorResult.wasCreated) {
        console.log('🚫 UPDATE_EXTRUSION_CONNECTOR_SKIPPED:', safeConnectorResult.reason);
        // Don't add this connector, but continue processing
      } else {
      
      const newUpdatedConnector = safeConnectorResult.connector!;
      
      // 🔍 COMPREHENSIVE LOGGING: Track wall creation with ownership details
      console.log('🔍 [updateExistingExtrusion] UPDATED_CONNECTOR_CREATED:', {
        wallCoords: `(${splitPoint.x.toFixed(1)},${splitPoint.y.toFixed(1)})->(${splitPointNew.x.toFixed(1)},${splitPointNew.y.toFixed(1)})`,
        splitWallOwnership: {
          originatingRoomId: (splitWall as any).originatingRoomId || 'NONE',
          assignedRoomIds: (splitWall as any).assignedRoomIds || 'NONE'
        },
        newWallOwnership: {
          originatingRoomId: newUpdatedConnector.originatingRoomId || 'NONE',
          assignedRoomIds: newUpdatedConnector.assignedRoomIds || 'NONE'
        },
        hasOwnership: !!(newUpdatedConnector.originatingRoomId && newUpdatedConnector.assignedRoomIds),
        timestamp: Date.now(),
        stackTrace: new Error().stack?.split('\n').slice(1, 4).join(' → ')
      });
      
      updatedConnectors.push(newUpdatedConnector);
      
      console.log(`[updateExistingExtrusion] 🔧 SPLIT POINT CONNECTOR: Created connector from split point (${splitPoint.x.toFixed(1)},${splitPoint.y.toFixed(1)}) to (${splitPointNew.x.toFixed(1)},${splitPointNew.y.toFixed(1)})`);
      }
    }
  } else {
      console.log(`[updateExistingExtrusion] 🔧 ORIGINAL ENDPOINT PROTECTION: Split point is actually an original room endpoint - no connector created`);
    }
  }
  
  // 🔧 CRITICAL: For ALL endpoints (both split and original), ensure connected walls extend/retract naturally
  // This is the key fix - original room endpoints should ALWAYS allow wall extension/retraction
  const handleEndpointExtension = (originalEndpoint: Point, newEndpoint: Point, endpointType: string) => {
    const tolerance = 0.1;
    
    // Find walls connected to this endpoint (excluding the segment being dragged and extrusion connectors)
    const connectedWalls = walls.filter(wall => {
      if (wall === splitWall) return false;
      if (wall.isExtrusionConnector) return false;
      
      const connectsAtStart = Math.abs(wall.start.x - originalEndpoint.x) < tolerance && Math.abs(wall.start.y - originalEndpoint.y) < tolerance;
      const connectsAtEnd = Math.abs(wall.end.x - originalEndpoint.x) < tolerance && Math.abs(wall.end.y - originalEndpoint.y) < tolerance;
      
      return connectsAtStart || connectsAtEnd;
    });
    
    console.log(`[updateExistingExtrusion] 🔧 ${endpointType} ENDPOINT EXTENSION: Found ${connectedWalls.length} walls to extend/retract`, {
      originalEndpoint: `(${originalEndpoint.x.toFixed(1)},${originalEndpoint.y.toFixed(1)})`,
      newEndpoint: `(${newEndpoint.x.toFixed(1)},${newEndpoint.y.toFixed(1)})`,
      connectedWalls: connectedWalls.map(w => `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`)
    });
    
    // Extend/retract each connected wall to maintain room shape
    connectedWalls.forEach(wall => {
      const wallConnectsAtStart = Math.abs(wall.start.x - originalEndpoint.x) < tolerance && Math.abs(wall.start.y - originalEndpoint.y) < tolerance;
      const wallConnectsAtEnd = Math.abs(wall.end.x - originalEndpoint.x) < tolerance && Math.abs(wall.end.y - originalEndpoint.y) < tolerance;
      
      if (wallConnectsAtStart) {
        // Move the start point of the connected wall to the new position
        wall.start.x = newEndpoint.x;
        wall.start.y = newEndpoint.y;
        console.log(`[updateExistingExtrusion] 🔧 EXTENDED WALL START: Wall (${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)}) extended to start at (${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})`);
      } else if (wallConnectsAtEnd) {
        // Move the end point of the connected wall to the new position
        wall.end.x = newEndpoint.x;
        wall.end.y = newEndpoint.y;
        console.log(`[updateExistingExtrusion] 🔧 EXTENDED WALL END: Wall (${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)}) extended to end at (${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`);
      }
    });
  };
  
  // 🔧 ENHANCED: Also handle indirect connections through existing connectors
  const handleConnectorWallUpdates = (originalEndpoint: Point, newEndpoint: Point, endpointType: string) => {
    const tolerance = 0.1;
    
    // Find connector walls that need to be updated
    const connectorWalls = walls.filter(wall => {
      if (wall === splitWall) return false;
      if (!wall.isExtrusionConnector) return false;
      
      const connectsAtStart = Math.abs(wall.start.x - originalEndpoint.x) < tolerance && Math.abs(wall.start.y - originalEndpoint.y) < tolerance;
      const connectsAtEnd = Math.abs(wall.end.x - originalEndpoint.x) < tolerance && Math.abs(wall.end.y - originalEndpoint.y) < tolerance;
      
      return connectsAtStart || connectsAtEnd;
    });
    
    console.log(`[updateExistingExtrusion] 🔧 ${endpointType} CONNECTOR UPDATE: Found ${connectorWalls.length} connector walls to update`, {
      originalEndpoint: `(${originalEndpoint.x.toFixed(1)},${originalEndpoint.y.toFixed(1)})`,
      newEndpoint: `(${newEndpoint.x.toFixed(1)},${newEndpoint.y.toFixed(1)})`,
      connectorWalls: connectorWalls.map(w => `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`)
    });
    
    // Update each connector wall endpoint
    connectorWalls.forEach(wall => {
      const wallConnectsAtStart = Math.abs(wall.start.x - originalEndpoint.x) < tolerance && Math.abs(wall.start.y - originalEndpoint.y) < tolerance;
      const wallConnectsAtEnd = Math.abs(wall.end.x - originalEndpoint.x) < tolerance && Math.abs(wall.end.y - originalEndpoint.y) < tolerance;
      
      if (wallConnectsAtStart) {
        wall.start.x = newEndpoint.x;
        wall.start.y = newEndpoint.y;
        console.log(`[updateExistingExtrusion] 🔧 UPDATED CONNECTOR START: Connector moved to start at (${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})`);
      } else if (wallConnectsAtEnd) {
        wall.end.x = newEndpoint.x;
        wall.end.y = newEndpoint.y;
        console.log(`[updateExistingExtrusion] 🔧 UPDATED CONNECTOR END: Connector moved to end at (${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`);
      }
    });
  };
  
  // Apply extension logic ONLY to the endpoint that is NOT the split point
  // The split point should stay fixed with connectors, while the original room endpoint should extend walls
  if (splitPoint) {
    const splitPointIsP1 = Math.abs(splitPoint.x - originalSplitStart.x) < 0.1 && Math.abs(splitPoint.y - originalSplitStart.y) < 0.1;
    const splitPointIsP2 = Math.abs(splitPoint.x - originalSplitEnd.x) < 0.1 && Math.abs(splitPoint.y - originalSplitEnd.y) < 0.1;
    
    console.log(`[updateExistingExtrusion] 🔧 SELECTIVE ENDPOINT EXTENSION: splitPointIsP1=${splitPointIsP1}, splitPointIsP2=${splitPointIsP2}`);
    
    if (splitPointIsP1) {
      // P1 is the split point (stays fixed with connectors), P2 is the original endpoint (extends walls)
      handleEndpointExtension(originalSplitEnd, P2_new, "P2");
      handleConnectorWallUpdates(originalSplitEnd, P2_new, "P2");
      console.log(`[updateExistingExtrusion] 🔧 P1 is split point (fixed), P2 is original endpoint (extends walls)`);
    } else if (splitPointIsP2) {
      // P2 is the split point (stays fixed with connectors), P1 is the original endpoint (extends walls)
      handleEndpointExtension(originalSplitStart, P1_new, "P1");
      handleConnectorWallUpdates(originalSplitStart, P1_new, "P1");
      console.log(`[updateExistingExtrusion] 🔧 P2 is split point (fixed), P1 is original endpoint (extends walls)`);
    } else {
      // Fallback: if we can't determine which is the split point, apply extension to both (old behavior)
      handleEndpointExtension(originalSplitStart, P1_new, "P1");
      handleConnectorWallUpdates(originalSplitStart, P1_new, "P1");
      handleEndpointExtension(originalSplitEnd, P2_new, "P2");
      handleConnectorWallUpdates(originalSplitEnd, P2_new, "P2");
      console.log(`[updateExistingExtrusion] 🔧 FALLBACK: Could not determine split point, applying extension to both endpoints`);
    }
  } else {
    // No split point detected, apply extension to both endpoints (old behavior)
    handleEndpointExtension(originalSplitStart, P1_new, "P1");
    handleConnectorWallUpdates(originalSplitStart, P1_new, "P1");
    handleEndpointExtension(originalSplitEnd, P2_new, "P2");
    handleConnectorWallUpdates(originalSplitEnd, P2_new, "P2");
    console.log(`[updateExistingExtrusion] 🔧 No split point detected, applying extension to both endpoints`);
  }
  
  // 🔧 FIXED: Mark the moved segment as main extrusion segment
  splitWall.isMainExtrusionSegment = true;
  
  // 🔧 ENHANCED: Return all room walls including the unchanged ones
  // Filter out only the split wall (which we'll include separately) but keep all other walls including updated connectors
  const otherRoomWalls = walls.filter(wall => {
    // Exclude the split wall we just moved (by reference since it's the same object)
    if (wall === splitWall) return false;
    
    // 🔧 CRITICAL: DON'T filter out existing connectors since they may have been updated in place
    // The handleConnectorWallUpdates function updates existing connector walls directly
    // So we want to keep ALL walls except the split wall itself
    
    return true; // Keep all other walls, including any updated connectors
  });
  
  // 🔧 ENHANCED: Only add new connectors that were actually created
  // Since we now keep existing connectors that were updated in place, we only add truly new ones
  const result = [...otherRoomWalls, splitWall, ...updatedConnectors];
  
  console.log(`[updateExistingExtrusion] Updated extrusion with all room walls`, {
    totalWallsReturned: result.length,
    originalRoomWalls: otherRoomWalls.length,
    movedSplitWall: 1,
    newConnectors: updatedConnectors.length,
    existingConnectorsUpdatedInPlace: existingConnectors.length,
    mainExtrusionSegment: `(${splitWall.start.x.toFixed(1)},${splitWall.start.y.toFixed(1)})->(${splitWall.end.x.toFixed(1)},${splitWall.end.y.toFixed(1)})`,
    newConnectorCoords: updatedConnectors.map(c => `(${c.start.x.toFixed(1)},${c.start.y.toFixed(1)})->(${c.end.x.toFixed(1)},${c.end.y.toFixed(1)})`),
    existingConnectorCoords: existingConnectors.map(c => `(${c.start.x.toFixed(1)},${c.start.y.toFixed(1)})->(${c.end.x.toFixed(1)},${c.end.y.toFixed(1)})`),
    splitPointBehavior: splitPoint ? 'Only split point has connector' : 'Both endpoints have connectors (fallback)',
    updateStrategy: existingConnectors.length > 0 ? 'Updated existing connectors in place + endpoint extension' : 'Created new connectors + endpoint extension'
  });
  
  // Return all room walls: all existing walls (including updated connectors) + moved split wall + any new connectors
  return result;
};

/**
 * Validates wall set to ensure no walls are lost during resize
 */
function validateWallSetIntegrity(
  originalWalls: Wall[],
  resizedWalls: Wall[],
  operation: string
): { isValid: boolean; missingWalls: number; issues: string[] } {
  const issues: string[] = [];
  const missingWalls = originalWalls.length - resizedWalls.length;
  
  if (missingWalls > 0) {
    issues.push(`${missingWalls} walls lost during ${operation}`);
    console.error(`🚨 WALL_DELETION_DETECTED:`, {
      operation,
      originalCount: originalWalls.length,
      resizedCount: resizedWalls.length,
      missingWalls,
      originalWalls: originalWalls.map(w => ({
        tempId: (w as any).tempId,
        coords: `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`,
        metadata: {
          isSplitOrigin: (w as any).isSplitOrigin,
          isMainExtrusionSegment: (w as any).isMainExtrusionSegment,
          isExtrusionConnector: (w as any).isExtrusionConnector
        }
      })),
      resizedWalls: resizedWalls.map(w => ({
        tempId: (w as any).tempId,
        coords: `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`
      }))
    });
  }
  
  // Check for metadata loss
  let metadataLossCount = 0;
  resizedWalls.forEach((resizedWall, index) => {
    const originalWall = originalWalls[index];
    if (!originalWall) return;
    
    const hasOriginalMetadata = (originalWall as any).originatingRoomId || 
                                (originalWall as any).assignedRoomIds?.length || 
                                (originalWall as any).parentRoomIds?.length;
    const hasResizedMetadata = (resizedWall as any).originatingRoomId || 
                               (resizedWall as any).assignedRoomIds?.length || 
                               (resizedWall as any).parentRoomIds?.length;
    
    if (hasOriginalMetadata && !hasResizedMetadata) {
      metadataLossCount++;
    }
  });
  
  if (metadataLossCount > 0) {
    issues.push(`${metadataLossCount} walls lost metadata during ${operation}`);
  }
  
  return {
    isValid: issues.length === 0,
    missingWalls,
    issues
  };
}

export const calculateResizedWalls = (
  roomWallsInput: Wall[],          // All walls of the selected room as they were when resize started
  draggedWallSegmentProp: Wall,   // The specific segment that was initially clicked for dragging
  resizeHandleDirection: string, // 'n', 's', 'e', 'w', or corner handles like 'ne', 'nw', 'se', 'sw'
  targetPoint: Point,             // Current mouse/touch point where the edge/corner should move
  isFinalAdjustment: boolean = false, // Indicates if this is the final adjustment on mouse/touch up
  maintainSquareness: boolean = false, // Optional: for corner drags to maintain squareness
  originalRoomAspectRatio?: number,    // Optional: for corner drags to maintain aspect ratio
  forceStandardResize: boolean = false, // If true, ignore split wall properties and use standard resize
  allAvailableWalls?: Wall[] // ⚡ NEW: Additional walls for connector detection (tempWalls + currentWalls)
): Wall[] => {
  
  // 🔧 DEBUG: Track function entry to see what flags are set
  console.log('🔧 [calculateResizedWalls] ENTRY', {
    draggedWallCoords: `(${draggedWallSegmentProp.start.x.toFixed(1)},${draggedWallSegmentProp.start.y.toFixed(1)})->(${draggedWallSegmentProp.end.x.toFixed(1)},${draggedWallSegmentProp.end.y.toFixed(1)})`,
    draggedWallFlags: {
      isSplitOrigin: (draggedWallSegmentProp as any).isSplitOrigin,
      isMainExtrusionSegment: (draggedWallSegmentProp as any).isMainExtrusionSegment,
      parentWallId: (draggedWallSegmentProp as any).parentWallId,
      requiresExtrusionOnMove: (draggedWallSegmentProp as any).requiresExtrusionOnMove
    },
    roomWallsCount: roomWallsInput.length,
    resizeDirection: resizeHandleDirection,
    targetPoint: `(${targetPoint.x.toFixed(1)},${targetPoint.y.toFixed(1)})`,
    forceStandardResize
  });
  // console.log(`[calculateResizedWalls] Entry. Dragged Prop Start: ${JSON.stringify(draggedWallSegmentProp.start)} End: ${JSON.stringify(draggedWallSegmentProp.end)} Direction: ${resizeHandleDirection}`);

  // 🔧 ENHANCED: Check if this is a split wall using multiple indicators, but respect forceStandardResize flag
  const isSplitWall = !forceStandardResize && ((draggedWallSegmentProp as any).isSplitOrigin || (draggedWallSegmentProp as any).requiresExtrusionOnMove);
  const hasParentRoomIds = (draggedWallSegmentProp as any).parentRoomIds && Array.isArray((draggedWallSegmentProp as any).parentRoomIds);
  
  if (forceStandardResize) {
    console.log('[calculateResizedWalls] 🔧 FORCED STANDARD RESIZE: Ignoring split wall properties due to forceStandardResize flag', {
      wallStart: draggedWallSegmentProp.start,
      wallEnd: draggedWallSegmentProp.end,
      isSplitOrigin: (draggedWallSegmentProp as any).isSplitOrigin,
      requiresExtrusionOnMove: (draggedWallSegmentProp as any).requiresExtrusionOnMove,
      resizeDirection: resizeHandleDirection,
      targetPoint,
      reason: 'Room has existing extrusions, preventing new extrusion creation for isSplitOrigin walls'
    });
  }
  
  // 🔧 SIMPLIFIED: Check if this is an existing main extrusion segment
  const isMainExtrusionSegment = (draggedWallSegmentProp as any).isMainExtrusionSegment;
  
  if (isSplitWall && !isMainExtrusionSegment) {
    // 🔧 FIX: Check if split wall is actually being moved away from its original position
    // Only create extrusion if there's significant movement, otherwise treat as normal resize
    const originalStart = draggedWallSegmentProp.start;
    const originalEnd = draggedWallSegmentProp.end;
    
    // Calculate where the wall would move to
    let newStart = { ...originalStart };
    let newEnd = { ...originalEnd };
    
    if (resizeHandleDirection === 'n' || resizeHandleDirection === 's') {
      newStart.y = targetPoint.y;
      newEnd.y = targetPoint.y;
    } else if (resizeHandleDirection === 'w' || resizeHandleDirection === 'e') {
      newStart.x = targetPoint.x;
      newEnd.x = targetPoint.x;
    }
    
    // Check if there's significant movement (> 5 pixels)
    const movementDistance = Math.abs(
      (resizeHandleDirection === 'n' || resizeHandleDirection === 's') 
        ? targetPoint.y - originalStart.y 
        : targetPoint.x - originalStart.x
    );
    
    const MIN_EXTRUSION_DISTANCE = 5; // pixels
    
    if (movementDistance > MIN_EXTRUSION_DISTANCE) {
      console.log('[calculateResizedWalls] 🔧 EXTRUSION: Split wall moved significantly - creating NEW extrusion', {
        wallStart: draggedWallSegmentProp.start,
        wallEnd: draggedWallSegmentProp.end,
        movementDistance,
        minRequired: MIN_EXTRUSION_DISTANCE,
        resizeDirection: resizeHandleDirection,
        targetPoint
      });
      
      // 🔧 DEBUG: About to call calculateSplitWallExtrusion
      console.log('🔧 [calculateResizedWalls] ABOUT_TO_CALL_calculateSplitWallExtrusion', {
        wallBeingDragged: `(${draggedWallSegmentProp.start.x.toFixed(1)},${draggedWallSegmentProp.start.y.toFixed(1)})->(${draggedWallSegmentProp.end.x.toFixed(1)},${draggedWallSegmentProp.end.y.toFixed(1)})`,
        roomWallsCount: roomWallsInput.length
      });
      
      // Returns all room walls including the extrusion (moved split wall + connectors + unchanged walls)
      // For inward resize, calculateSplitWallExtrusion returns null to indicate standard resize should be used
      const extrusionResult = calculateSplitWallExtrusion(roomWallsInput, draggedWallSegmentProp, resizeHandleDirection, targetPoint, allAvailableWalls);
      if (extrusionResult !== null) {
        return extrusionResult;
      }
      // If null returned, fall through to standard resize logic below
      console.log('[calculateResizedWalls] 🔧 INWARD RESIZE: Falling through to standard resize logic');
      // Continue with standard resize logic below
    } else {
      console.log('[calculateResizedWalls] 🔧 STANDARD: Split wall movement too small - treating as standard resize', {
        wallStart: draggedWallSegmentProp.start,
        wallEnd: draggedWallSegmentProp.end,
        movementDistance,
        minRequired: MIN_EXTRUSION_DISTANCE,
        resizeDirection: resizeHandleDirection,
        targetPoint
      });
      // Fall through to standard resize logic
    }
  }
  
  if (isMainExtrusionSegment) {
    console.log('[calculateResizedWalls] 🔧 EXTRUSION: Main extrusion segment detected - moving independently', {
      wallStart: draggedWallSegmentProp.start,
      wallEnd: draggedWallSegmentProp.end,
      tempId: (draggedWallSegmentProp as any).tempId,
      resizeDirection: resizeHandleDirection,
      targetPoint
    });
    // 🔧 FIX: Move only the extrusion segment and its connectors, keep main room stable
    return moveMainExtrusionSegmentOnly(roomWallsInput, draggedWallSegmentProp, resizeHandleDirection, targetPoint);
  }

  let newWallsWorkingCopy: Wall[] = JSON.parse(JSON.stringify(roomWallsInput)); // Deep copy to work with

  // Helper to find a wall in the working copy that matches a reference wall
  const findWallInWorkingCopy = (refWall: Wall, walls: Wall[]): Wall | undefined => {
    return walls.find(w => 
      (w.start.x === refWall.start.x && w.start.y === refWall.start.y && w.end.x === refWall.end.x && w.end.y === refWall.end.y) ||
      (w.start.x === refWall.end.x && w.start.y === refWall.end.y && w.end.x === refWall.start.x && w.end.y === refWall.start.y)
    );
  };

  const segmentBeingDragged = findWallInWorkingCopy(draggedWallSegmentProp, newWallsWorkingCopy);

  if (!segmentBeingDragged) {
    console.error('[calculateResizedWalls] Dragged wall segment not found in working copy. This should not happen.');
    return roomWallsInput; // Return original if something is wrong
  }
  // console.log(`[calculateResizedWalls] Identified segmentBeingDragged in newWallsWorkingCopy: Start ${JSON.stringify(segmentBeingDragged.start)} End ${JSON.stringify(segmentBeingDragged.end)}`);


  // Simplified geometry helper functions (assuming they might be needed or defined elsewhere)
  const distance = (p1: Point, p2: Point) => Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2));
  const crossProduct = (o: Point, a: Point, b: Point) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

  // Function to check if a point is on a line segment (within a tolerance)
  const isPointOnLineSegment = (p: Point, a: Point, b: Point, tolerance = 0.1): boolean => {
    const dAP = distance(a, p);
    const dPB = distance(p, b);
    const dAB = distance(a, b);
    return Math.abs(dAP + dPB - dAB) < tolerance;
  };
  
  const getLineIntersection = (p1: Point, p2: Point, p3: Point, p4: Point): Point | null => {
    const den = (p1.x - p2.x) * (p3.y - p4.y) - (p1.y - p2.y) * (p3.x - p4.x);
    if (den === 0) return null; // Parallel or collinear
    const t = ((p1.x - p3.x) * (p3.y - p4.y) - (p1.y - p3.y) * (p3.x - p4.x)) / den;
    const u = -((p1.x - p2.x) * (p1.y - p3.y) - (p1.y - p2.y) * (p1.x - p3.x)) / den;

    if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
      return { x: p1.x + t * (p2.x - p1.x), y: p1.y + t * (p2.y - p1.y) };
    }
    return null; // Intersection is outside the segments
  };


  const findCollinearSiblingsAtPoint = (point: Point, currentWall: Wall, allWalls: Wall[]): Wall[] => {
    const connectedCollinear = [];
    for (const wall of allWalls) {
      if (wall === currentWall) continue; // Skip itself

      const sharesPoint = (wall.start.x === point.x && wall.start.y === point.y) ||
                          (wall.end.x === point.x && wall.end.y === point.y);
      if (!sharesPoint) continue;

      // Check for collinearity: orientation should be 0 (or very close)
      // Three points p, q, r are collinear if orientation(p, q, r) is 0.
      // Use currentWall's other point, the shared point, and the potential sibling's other point.
      const otherPointCurrent = (currentWall.start.x === point.x && currentWall.start.y === point.y) ? currentWall.end : currentWall.start;
      const otherPointSibling = (wall.start.x === point.x && wall.start.y === point.y) ? wall.end : wall.start;
      
      // Check orientation(otherPointCurrent, point, otherPointSibling)
      const val = (point.y - otherPointCurrent.y) * (otherPointSibling.x - point.x) -
                  (point.x - otherPointCurrent.x) * (otherPointSibling.y - point.y);

      if (Math.abs(val) < 1e-3) { // Tolerance for floating point collinearity
         // Ensure they extend in opposite directions or the same for true "sibling" behavior in indent cases
        const dotProduct = (point.x - otherPointCurrent.x) * (otherPointSibling.x - point.x) +
                           (point.y - otherPointCurrent.y) * (otherPointSibling.y - point.y);
        // If dotProduct is negative, they extend in roughly opposite directions from the shared point.
        // If positive, they extend in roughly the same direction (overlap or extension)
        // For indent scenarios, we often look for opposite extensions.
        if (dotProduct < 0) { 
          connectedCollinear.push(wall);
        }
      }
    }
    return connectedCollinear;
  };
  
  const getProjectionOntoLine = (point: Point, lineStart: Point, lineEnd: Point): Point => {
    const l2 = (lineEnd.x - lineStart.x)**2 + (lineEnd.y - lineStart.y)**2;
    if (l2 === 0) return lineStart; // Avoid division by zero if line is a point
    const t = ((point.x - lineStart.x) * (lineEnd.x - lineStart.x) + (point.y - lineStart.y) * (lineEnd.y - lineStart.y)) / l2;
    return {
      x: lineStart.x + t * (lineEnd.x - lineStart.x),
      y: lineStart.y + t * (lineEnd.y - lineStart.y)
    };
  };


  const P1_orig = segmentBeingDragged.start;
  const P2_orig = segmentBeingDragged.end;
  let P1_new = { ...P1_orig };
  let P2_new = { ...P2_orig };

  // Adjust P1_new and P2_new based on resizeHandleDirection and targetPoint
  if (resizeHandleDirection === 'n') {
    P1_new.y = targetPoint.y;
    P2_new.y = targetPoint.y;
  } else if (resizeHandleDirection === 's') {
    P1_new.y = targetPoint.y;
    P2_new.y = targetPoint.y;
  } else if (resizeHandleDirection === 'w') {
    P1_new.x = targetPoint.x;
    P2_new.x = targetPoint.x;
  } else if (resizeHandleDirection === 'e') {
    P1_new.x = targetPoint.x;
    P2_new.x = targetPoint.x;
  } else if (['nw', 'ne', 'sw', 'se'].includes(resizeHandleDirection)) {
    // Corner drag: behavior depends on which points of the segmentBeingDragged form the corner
    // This part needs careful implementation based on how corners are defined relative to segmentBeingDragged
    // For now, let's assume a simple case if segmentBeingDragged itself is part of the corner definition
    if (resizeHandleDirection === 'nw') { // Top-left corner
        P1_new = { x: targetPoint.x, y: targetPoint.y }; // Assuming P1 is the corner
        // Need to decide how P2 moves. If maintaining squareness, P2_new.x = P2_orig.x, P2_new.y = targetPoint.y
        // or P2_new.x = targetPoint.x, P2_new.y = P2_orig.y
        // This part is complex and depends on the desired UX for corner dragging.
    } else if (resizeHandleDirection === 'ne') { // Top-right
        P2_new = { x: targetPoint.x, y: targetPoint.y }; // Assuming P2 is the corner
    } else if (resizeHandleDirection === 'sw') { // Bottom-left
        // Similar logic
    } else if (resizeHandleDirection === 'se') { // Bottom-right
        // Similar logic
    }
    // For a simple interpretation where the whole room might scale from a corner:
    // This is a placeholder; proper corner resize is more involved.
    if (P1_orig.x === segmentBeingDragged.start.x && P1_orig.y === segmentBeingDragged.start.y) P1_new = targetPoint;
    if (P2_orig.x === segmentBeingDragged.end.x && P2_orig.y === segmentBeingDragged.end.y) P2_new = targetPoint;


  }


  segmentBeingDragged.start = P1_new;
  segmentBeingDragged.end = P2_new;

  // 🔧 CRITICAL FIX: Replace exact coordinate matching with tolerance-based endpoint snapping
  // to prevent wall drift during repeated split/resize operations
  const ENDPOINT_SNAP_TOLERANCE = 5; // pixels
  
  // Helper function for tolerance-based point matching
  const arePointsNear = (p1: Point, p2: Point, tolerance = ENDPOINT_SNAP_TOLERANCE): boolean => {
    return Math.abs(p1.x - p2.x) <= tolerance && Math.abs(p1.y - p2.y) <= tolerance;
  };

  // Update walls connected to the dragged wall's original endpoints
  newWallsWorkingCopy.forEach(wall => {
    if (wall === segmentBeingDragged) return; // Skip the dragged wall itself

    // Update connections to P1_orig (with tolerance-based matching)
    if (arePointsNear(wall.start, P1_orig)) {
      wall.start = { ...P1_new }; // Snap to new position
    } else if (arePointsNear(wall.end, P1_orig)) {
      wall.end = { ...P1_new }; // Snap to new position
    }

    // Update connections to P2_orig (with tolerance-based matching)
    if (arePointsNear(wall.start, P2_orig)) {
      wall.start = { ...P2_new }; // Snap to new position
    } else if (arePointsNear(wall.end, P2_orig)) {
      wall.end = { ...P2_new }; // Snap to new position
    }
  });
  
  // INDENT/PROTRUSION LOGIC (Simplified Example)
  // This is where the complex logic for handling indents/protrusions would go.
  // It involves checking for collinear walls and adjusting them.
  const MIN_WALL_LENGTH = 1; // Example minimum wall length

  const originalSiblingsAtP1 = findCollinearSiblingsAtPoint(P1_orig, draggedWallSegmentProp, roomWallsInput);
  const originalSiblingsAtP2 = findCollinearSiblingsAtPoint(P2_orig, draggedWallSegmentProp, roomWallsInput);

  // console.log(`[calculateResizedWalls] Original sibling at P1_orig (prop): ${originalSiblingsAtP1.length > 0 ? JSON.stringify(originalSiblingsAtP1[0]) : 'None'}`);
  // console.log(`[calculateResizedWalls] Original sibling at P2_orig (prop): ${originalSiblingsAtP2.length > 0 ? JSON.stringify(originalSiblingsAtP2[0]) : 'None'}`);


  if (originalSiblingsAtP1.length === 1 && originalSiblingsAtP2.length === 1) {
    // console.log("[IndentResize] Two collinear siblings detected (potential indent/protrusion center piece).");
    // This is a complex case: segmentBeingDragged is between two other collinear segments.
    // Adjusting this typically means the two "outer" walls (the siblings) also need to change length.
    const sibling1 = findWallInWorkingCopy(originalSiblingsAtP1[0], newWallsWorkingCopy);
    const sibling2 = findWallInWorkingCopy(originalSiblingsAtP2[0], newWallsWorkingCopy);

    if (sibling1 && sibling2) {
        // If P1_orig was sibling1.start, then sibling1.start becomes P1_new.
        // If P1_orig was sibling1.end,   then sibling1.end   becomes P1_new.
        if(sibling1.start.x === P1_orig.x && sibling1.start.y === P1_orig.y) sibling1.start = P1_new; else sibling1.end = P1_new;
        if(sibling2.start.x === P2_orig.x && sibling2.start.y === P2_orig.y) sibling2.start = P2_new; else sibling2.end = P2_new;
        
        // Make sure siblings don't become too short or flip
        if (distance(sibling1.start, sibling1.end) < MIN_WALL_LENGTH) { /* revert or adjust */ }
        if (distance(sibling2.start, sibling2.end) < MIN_WALL_LENGTH) { /* revert or adjust */ }
    }
  } else if (originalSiblingsAtP1.length === 1 || originalSiblingsAtP2.length === 1) {
    // console.log("[IndentResize] One collinear sibling detected. THIS LOGIC PATH NEEDS REVIEW/REWORK.");
    // This means segmentBeingDragged is an "outer" piece of an indent/protrusion.
    // The sibling is the "middle" piece. The wall "parallel" to the sibling also needs to move.
    let movedPoint: Point, stationaryPointOfDragged: Point, siblingWallRef: Wall, siblingAttachPoint: Point;

    if (originalSiblingsAtP1.length === 1) {
        movedPoint = P1_new;
        stationaryPointOfDragged = P2_new; // This point of the dragged wall doesn't have a collinear sibling here
        siblingWallRef = originalSiblingsAtP1[0];
        siblingAttachPoint = P1_orig;
    } else { // originalSiblingsAtP2.length === 1
        movedPoint = P2_new;
        stationaryPointOfDragged = P1_new;
        siblingWallRef = originalSiblingsAtP2[0];
        siblingAttachPoint = P2_orig;
    }
    
    const siblingWall = findWallInWorkingCopy(siblingWallRef, newWallsWorkingCopy);
    if (siblingWall) {
        // The sibling wall's endpoint that was connected to segmentBeingDragged moves with it.
        if(siblingWall.start.x === siblingAttachPoint.x && siblingWall.start.y === siblingAttachPoint.y) siblingWall.start = movedPoint; else siblingWall.end = movedPoint;

        // Now find the wall parallel to siblingWall that forms the other side of the "indent cavity"
        // This is tricky. For a simple rectangular indent, it's the wall that shares an endpoint with siblingWall's *other* end,
        // and is parallel to segmentBeingDragged.
        
        // Simplified: Assume the room is mostly rectangular or L-shaped.
        // The two walls connected to stationaryPointOfDragged also need their common point adjusted
        // to stay perpendicular (or original angle) to the newly positioned segmentBeingDragged.
        newWallsWorkingCopy.forEach(adjWall => {
            if (adjWall === segmentBeingDragged || adjWall === siblingWall) return;

            let connectedToStationary = false;
            let otherEndOfAdjWall: Point | null = null;

            if (adjWall.start.x === stationaryPointOfDragged.x && adjWall.start.y === stationaryPointOfDragged.y) {
                connectedToStationary = true;
                otherEndOfAdjWall = adjWall.end;
            } else if (adjWall.end.x === stationaryPointOfDragged.x && adjWall.end.y === stationaryPointOfDragged.y) {
                connectedToStationary = true;
                otherEndOfAdjWall = adjWall.start;
            }

            if (connectedToStationary && otherEndOfAdjWall) {
                // This adjWall is connected to the "stationary" end of the dragged segment.
                // Its other end needs to move to maintain the room's shape.
                // Project this otherEndOfAdjWall onto the new line defined by the moved sibling.
                const siblingOtherEnd = (siblingWall.start.x === movedPoint.x && siblingWall.start.y === movedPoint.y) ? siblingWall.end : siblingWall.start;
                
                // Determine if dragged wall is horizontal or vertical
                const draggedIsHorizontal = Math.abs(P1_new.y - P2_new.y) < 1e-3; // Using P1_new, P2_new from segmentBeingDragged
                const draggedIsVertical = Math.abs(P1_new.x - P2_new.x) < 1e-3;

                if (draggedIsHorizontal) { // Dragged wall is horizontal, adjWall should be vertical
                    if (adjWall.start.x === stationaryPointOfDragged.x && adjWall.start.y === stationaryPointOfDragged.y) {
                        adjWall.end.x = otherEndOfAdjWall.x; // Keep original x
                        adjWall.end.y = siblingOtherEnd.y; // Align y with sibling's other end
                    } else { // adjWall.end is connected
                        adjWall.start.x = otherEndOfAdjWall.x;
                        adjWall.start.y = siblingOtherEnd.y;
                    }
                } else if (draggedIsVertical) { // Dragged wall is vertical, adjWall should be horizontal
                     if (adjWall.start.x === stationaryPointOfDragged.x && adjWall.start.y === stationaryPointOfDragged.y) {
                        adjWall.end.x = siblingOtherEnd.x; // Align x with sibling's other end
                        adjWall.end.y = otherEndOfAdjWall.y; // Keep original y
                    } else { // adjWall.end is connected
                        adjWall.start.x = siblingOtherEnd.x;
                        adjWall.start.y = otherEndOfAdjWall.y;
                    }
                }
            }
        });
    }
  } else {
    // console.log("[StandardResize] No collinear siblings. Performing standard wall push/pull.");
    // Standard resize: the two walls perpendicular to the dragged wall, and connected to its ends,
    // need to have their lengths adjusted.
    // Their other ends (not connected to the dragged wall) remain fixed.
    // The wall parallel to the dragged wall moves entirely.

    let parallelWall: Wall | undefined = undefined;
    const perpendicularWallsAtP1: Wall[] = [];
    const perpendicularWallsAtP2: Wall[] = [];

    // Find the parallel wall and perpendicular connections
    newWallsWorkingCopy.forEach(wall => {
        if (wall === segmentBeingDragged) return;

        const p1ConnectsAtStart = wall.start.x === P1_new.x && wall.start.y === P1_new.y;
        const p1ConnectsAtEnd = wall.end.x === P1_new.x && wall.end.y === P1_new.y;
        const p2ConnectsAtStart = wall.start.x === P2_new.x && wall.start.y === P2_new.y;
        const p2ConnectsAtEnd = wall.end.x === P2_new.x && wall.end.y === P2_new.y;

        if (p1ConnectsAtStart || p1ConnectsAtEnd) perpendicularWallsAtP1.push(wall);
        if (p2ConnectsAtStart || p2ConnectsAtEnd) perpendicularWallsAtP2.push(wall);
        
        // Check for parallelism (simplified: same x for vertical, same y for horizontal for other end points)
        // This is a very rough check for parallel in axis-aligned rectangles
        const isDraggedHorizontal = Math.abs(P1_new.y - P2_new.y) < 1e-3;
        const isDraggedVertical = Math.abs(P1_new.x - P2_new.x) < 1e-3;
        const isWallHorizontal = Math.abs(wall.start.y - wall.end.y) < 1e-3;
        const isWallVertical = Math.abs(wall.start.x - wall.end.x) < 1e-3;

        if (isDraggedHorizontal && isWallHorizontal && 
            !( (wall.start.y === P1_new.y && isPointOnLineSegment(wall.start, P1_new, P2_new)) || 
               (wall.end.y === P1_new.y && isPointOnLineSegment(wall.end, P1_new, P2_new)) ) &&
            !(perpendicularWallsAtP1.includes(wall) || perpendicularWallsAtP2.includes(wall)) // Ensure it's not one of the perpendiculars
            ) {
            // Check if wall's y is different, and if its x-range overlaps/is relevant
            if (Math.abs(wall.start.y - P1_new.y) > 1e-2) { // Different y-coordinate
                 parallelWall = wall;
            }
        } else if (isDraggedVertical && isWallVertical &&
            !( (wall.start.x === P1_new.x && isPointOnLineSegment(wall.start, P1_new, P2_new)) || 
               (wall.end.x === P1_new.x && isPointOnLineSegment(wall.end, P1_new, P2_new)) ) &&
            !(perpendicularWallsAtP1.includes(wall) || perpendicularWallsAtP2.includes(wall))
            ) {
            if (Math.abs(wall.start.x - P1_new.x) > 1e-2) { // Different x-coordinate
                parallelWall = wall;
            }
        }
    });
    
    if (parallelWall) {
        // Create a new constant that is definitely of type Wall.
        // This helps TypeScript's control flow analysis.
        const currentParallelWall: Wall = parallelWall;
        
        // Move the parallel wall
        // The original dx/dy calculations seemed unused for the corner drag, 
        // and edge drag used specific xOffset/yOffset.
        
        if (['nw', 'ne', 'sw', 'se'].includes(resizeHandleDirection)) {
          // This is complex. Need to determine which corner of parallelWall corresponds
          // For now, assume a simple translation based on targetPoint relative to an opposite corner.
          // Placeholder:
          if (resizeHandleDirection === 'n' || resizeHandleDirection === 's') {
               currentParallelWall.start.y = targetPoint.y - (P1_orig.y - currentParallelWall.start.y);
               currentParallelWall.end.y = targetPoint.y - (P1_orig.y - currentParallelWall.end.y);
          } else if (resizeHandleDirection === 'e' || resizeHandleDirection === 'w') {
               currentParallelWall.start.x = targetPoint.x - (P1_orig.x - currentParallelWall.start.x);
               currentParallelWall.end.x = targetPoint.x - (P1_orig.x - currentParallelWall.end.x);
          }

        } else { // Edge drag
             if (resizeHandleDirection === 'n' || resizeHandleDirection === 's') { // Moving horizontally dragged wall N/S
                // Correct: parallel wall maintains its original x coords but new y coord
                const yOffset = P1_new.y - P1_orig.y;
                currentParallelWall.start.y += yOffset;
                currentParallelWall.end.y += yOffset;

             } else if (resizeHandleDirection === 'e' || resizeHandleDirection === 'w') { // Moving vertically dragged wall E/W
                const xOffset = P1_new.x - P1_orig.x;
                currentParallelWall.start.x += xOffset;
                currentParallelWall.end.x += xOffset;
             }
        }
    } else {
        // console.warn("[StandardResize] Parallel wall not robustly found. Result might be skewed.");
    }

  }
  
  // 🛡️ CRITICAL: Preserve metadata from original walls before filtering
  // 🔧 FIX: Use coordinate-based matching instead of index-based to handle wall reordering
  const metadataPreservedWalls = newWallsWorkingCopy.map((wall, index) => {
    // Strategy 1: Try to find original wall by matching coordinates (handles wall reordering)
    const coordinateTolerance = 0.5;
    let originalWall = roomWallsInput.find(origWall => {
      // Check both forward and reverse direction matches
      const forwardMatch = 
        Math.abs(origWall.start.x - wall.start.x) < coordinateTolerance &&
        Math.abs(origWall.start.y - wall.start.y) < coordinateTolerance &&
        Math.abs(origWall.end.x - wall.end.x) < coordinateTolerance &&
        Math.abs(origWall.end.y - wall.end.y) < coordinateTolerance;
      
      const reverseMatch = 
        Math.abs(origWall.start.x - wall.end.x) < coordinateTolerance &&
        Math.abs(origWall.start.y - wall.end.y) < coordinateTolerance &&
        Math.abs(origWall.end.x - wall.start.x) < coordinateTolerance &&
        Math.abs(origWall.end.y - wall.start.y) < coordinateTolerance;
      
      return forwardMatch || reverseMatch;
    });
    
    // Strategy 2: Fallback to index-based matching if coordinate match fails
    if (!originalWall) {
      originalWall = roomWallsInput[index];
    }
    
    // Strategy 3: If still no match, check if this wall already has metadata (it's a new connector/extrusion)
    if (!originalWall) {
      // Wall doesn't exist in original set - likely a new connector or extrusion
      // Keep its existing metadata
      console.log('🔧 METADATA_PRESERVATION: New wall detected (connector/extrusion)', {
        coords: `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})->(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`,
        hasTempId: !!(wall as any).tempId,
        hasMetadata: !!((wall as any).originatingRoomId || (wall as any).assignedRoomIds)
      });
      return wall;
    }
    
    // Preserve all critical metadata from the matched original wall
    const preservedWall = {
      ...wall,
      tempId: (originalWall as any).tempId || (wall as any).tempId,
      originatingRoomId: (originalWall as any).originatingRoomId || (wall as any).originatingRoomId,
      assignedRoomIds: (originalWall as any).assignedRoomIds || (wall as any).assignedRoomIds,
      parentRoomIds: (originalWall as any).parentRoomIds || (wall as any).parentRoomIds,
      parentWallId: (originalWall as any).parentWallId || (wall as any).parentWallId,
      isSplitOrigin: (originalWall as any).isSplitOrigin || (wall as any).isSplitOrigin,
      isMainExtrusionSegment: (originalWall as any).isMainExtrusionSegment || (wall as any).isMainExtrusionSegment,
      isExtrusionConnector: (originalWall as any).isExtrusionConnector || (wall as any).isExtrusionConnector,
      connectedToSplitWallId: (originalWall as any).connectedToSplitWallId || (wall as any).connectedToSplitWallId,
      mainSegmentId: (originalWall as any).mainSegmentId || (wall as any).mainSegmentId
    };
    
    // Validate that we preserved the tempId
    if (!preservedWall.tempId) {
      console.warn('⚠️ METADATA_PRESERVATION: tempId missing after preservation', {
        wallIndex: index,
        coords: `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})->(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`,
        originalWallFound: !!originalWall,
        originalTempId: (originalWall as any).tempId
      });
    }
    
    return preservedWall;
  });
  
  // Final cleanup: Ensure wall start/end points are consistent if walls became zero length or invalid
  const cleanedWalls = metadataPreservedWalls.filter(wall => distance(wall.start, wall.end) >= MIN_WALL_LENGTH);
  
  // 🛡️ CRITICAL: Validate wall count to prevent deletion
  const validation = validateWallSetIntegrity(roomWallsInput, cleanedWalls, 'calculateResizedWalls');
  if (!validation.isValid) {
    console.error('🚨 RESIZE_VALIDATION_FAILED - Attempting recovery:', validation);
    
    // 🔧 RECOVERY: If walls were lost, try to preserve all walls that have valid length
    // This prevents catastrophic wall deletion during resize operations
    const recoveredWalls = metadataPreservedWalls.filter(wall => {
      const wallLength = distance(wall.start, wall.end);
      // Use a more lenient threshold for recovery
      return wallLength >= 0.01; // Allow very small walls instead of completely deleting them
    });
    
    console.log('🔧 RECOVERY_ATTEMPT:', {
      originalCount: roomWallsInput.length,
      cleanedCount: cleanedWalls.length,
      recoveredCount: recoveredWalls.length,
      recoveredWallsDetails: recoveredWalls.map(w => ({
        tempId: (w as any).tempId,
        length: distance(w.start, w.end).toFixed(2),
        coords: `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`
      }))
    });
    
    // Return recovered walls instead of cleaned walls to prevent deletion
    return recoveredWalls.length >= roomWallsInput.length - 1 ? recoveredWalls : cleanedWalls;
  }
  
  // Recalculate intersections if needed, especially for non-axis-aligned, but for now assume axis-aligned adjustments are okay.
  // If isFinalAdjustment, could do more rigorous geometry cleanup.

  // console.log(`[calculateResizedWalls] Returning ${cleanedWalls.length} walls.`);
  // cleanedWalls.forEach((w, i) => console.log(`  Wall ${i}: (${w.start.x.toFixed(1)}, ${w.start.y.toFixed(1)}) to (${w.end.x.toFixed(1)}, ${w.end.y.toFixed(1)})`));
  return cleanedWalls;
};

// Add other geometry-related utility functions here if extracted
// e.g., getPersistentRoomId, orientation, isPointOnLineSegment, getLineIntersection, etc.
// if they are general enough and not tightly coupled to FloorPlanCreator's state. 

// Add WallWithTempId if it's not already in types.ts or defined here
// Assuming it's primarily used with getUniqueCornerPoints for now.
// If types.ts defines Wall and Point, use those.
// import type { Point, Wall } from '../types'; // Ensure this is at the top

// If WallWithTempId is specific to utilities here, define it.
// Otherwise, it should ideally be in types.ts if used across modules.
// For now, assuming Point and Wall are imported from types.ts.
type WallWithTempId = import('../types').Wall & { tempId?: string };


export const getUniqueCornerPoints = (walls: WallWithTempId[]): import('../types').Point[] => {
    const allPoints: import('../types').Point[] = [];
    walls.forEach(wall => {
        allPoints.push(wall.start, wall.end);
    });

    const uniquePoints: import('../types').Point[] = [];
    const stringifiedPoints = new Set<string>();

    allPoints.forEach(point => {
        const s = `${point.x},${point.y}`;
        if (!stringifiedPoints.has(s)) {
            stringifiedPoints.add(s);
            uniquePoints.push(point);
        }
    });
    return uniquePoints;
}; 

/**
 * SHARED WALL FIX: Updates all copies of shared walls across all rooms
 * This prevents the "broken shared wall" issue when resizing newly connected rooms
 */
export const synchronizeSharedWallsAfterResize = (
  resizedWalls: Wall[],
  allWalls: Wall[],
  tolerance: number = 0.1
): Wall[] => {
  console.log('🔗 SYNC SHARED WALLS: Starting synchronization', {
    resizedWallsCount: resizedWalls.length,
    allWallsCount: allWalls.length
  });

  // Create a map of original -> new endpoint mappings from resized walls
  const endpointMappings: Array<{from: Point, to: Point}> = [];
  
  // Track which walls have been resized (by tempId) to avoid self-updates
  const resizedWallIds = new Set(resizedWalls.map(w => w.tempId));
  
  // Build endpoint mapping from the resize operation
  // We need to find which original endpoints moved to new positions
  resizedWalls.forEach(resizedWall => {
    // Find the corresponding original wall in allWalls to see what changed
    const originalWall = allWalls.find(w => w.tempId === resizedWall.tempId);
    if (!originalWall) return;
    
    // Track endpoint movements
    if (!arePointsEqual(originalWall.start, resizedWall.start, tolerance)) {
      endpointMappings.push({
        from: originalWall.start,
        to: resizedWall.start
      });
    }
    
    if (!arePointsEqual(originalWall.end, resizedWall.end, tolerance)) {
      endpointMappings.push({
        from: originalWall.end,
        to: resizedWall.end
      });
    }
  });
  
  console.log('🔗 SYNC SHARED WALLS: Detected endpoint movements', {
    movementsCount: endpointMappings.length,
    movements: endpointMappings.map(m => `(${m.from.x.toFixed(1)},${m.from.y.toFixed(1)}) -> (${m.to.x.toFixed(1)},${m.to.y.toFixed(1)})`)
  });

  // Apply endpoint mappings to all walls
  const synchronizedWalls = allWalls.map((wall, index) => {
    // Skip walls that were already resized
    if (resizedWallIds.has(wall.tempId)) {
      const resizedWall = resizedWalls.find(rw => rw.tempId === wall.tempId);
      return resizedWall || wall;
    }
    
    let newStart = wall.start;
    let newEnd = wall.end;
    let updated = false;
    
    // Check if this wall's endpoints need to be updated based on mappings
    endpointMappings.forEach(mapping => {
      if (arePointsEqual(wall.start, mapping.from, tolerance)) {
        newStart = {...mapping.to};
        updated = true;
      }
      if (arePointsEqual(wall.end, mapping.from, tolerance)) {
        newEnd = {...mapping.to};
        updated = true;
      }
    });
    
    if (updated) {
      console.log('🔗 SYNC SHARED WALLS: Updated connected wall', {
        wallIndex: index,
        tempId: wall.tempId,
        oldStart: wall.start,
        oldEnd: wall.end,
        newStart,
        newEnd
      });
      
      return {
        ...wall,
        start: newStart,
        end: newEnd
      };
    }
    
    return wall;
  });

  console.log('🔗 SYNC SHARED WALLS: Synchronization complete', {
    endpointMappingsApplied: endpointMappings.length,
    wallsChecked: allWalls.length,
    wallsUpdated: synchronizedWalls.filter((w, i) => w !== allWalls[i]).length
  });

  return synchronizedWalls;
};

/**
 * Helper function to check if two points are equal within tolerance
 */
const arePointsEqual = (p1: Point, p2: Point, tolerance: number): boolean => {
  return Math.abs(p1.x - p2.x) < tolerance && Math.abs(p1.y - p2.y) < tolerance;
};